<template>
  <div class="ar-tracker-master" :class="{'is-compact-mode': !isExpanded}" :style="teamColorVars">
    <!-- No enlarging on phones: the tracker already fills the width. -->
    <button v-if="!isMobileOnly && resizable" class="resize-tracker-btn" @click="toggleSize">
      <v-icon size="20" color="#fff">{{ isExpanded ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
    </button>
    <div v-if="tooltip.visible" class="tracker-custom-tooltip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
      {{ tooltip.text }}
    </div>

    <div v-if="showMomentum" class="momentum-header">
      <MomentumStats :event-id="eventId" :initial-sport-id="initialSportId" :matchData="matchData"/>
    </div>

    <div class="stage-master-container" :class="{ 'is-split-layout': isExpanded && mainViewTab !== 'live' && !isTennis && !isTableTennis && !isVolleyball }">

      <div class="holographic-stage" v-show="mainViewTab === 'live' || isTennis || isTableTennis || isVolleyball || isExpanded">
        <div class="stage-left-pitch">
          <div class="pitch-deploy-wrapper">
            <div class="pitch-stage-3d">
              <!-- Match-time progress: elapsed time out of the sport's total
                   duration, as a slim filled bar (same 0-1000 scale already
                   computed for the timeline labels). -->
              <!-- Football: full broadcast-style stadium (stands, ad-boards,
                   3D goals, camera following the ball, set-piece animations). -->
              <SoccerStadium v-if="isSoccerStadium"
                             :ball-x="ballPosition.x" :ball-y="ballPosition.y"
                             :ball-visible="!!displayAction && ballPosition.opacity > 0"
                             :action-type="displayAction ? displayAction.type : 0"
                             :action-side="displayAction ? displayAction.side : null"
                             :is-goal="!!displayAction && displayAction.isGoal"
                             :momentum-side="momentumSide" :momentum-visible="!!glowClass"
                             :badge-text="pitchBadgeText" :big-badge="bigBadge" :goal-celebration="goalCelebration"
                             :break-slides="breakSlides"
                             :time="stadiumClock" :progress="currentMatchProgressX / 10"
                             :logo-url="boardLogoUrl" :board-text="$t('live')"
                             :home-color="teamColors.home" :away-color="teamColors.away" />
              <!-- Tennis: broadcast side view of the court (score, serve fan,
                   who is serving). -->
              <TennisStadium v-else-if="isTennis"
                             :home-name="homeName" :away-name="awayName"
                             :points-h="currentGamePoints.h" :points-a="currentGamePoints.a"
                             :serving-side="servingSide" :is-tiebreak="!!tiebreakScore"
                             :surface="sportConfig.class"
                             :logo-url="boardLogoUrl" :board-text="$t('live')"
                             :home-color="teamColors.home" :away-color="teamColors.away"
                             :sets="tennisSets" :aces="aceCounts" :finished="scoreboardFinished" />
              <!-- Table tennis: same broadcast side view, on the table
                   (points, sets won, who is serving, cards / time-outs). -->
              <TableTennisArena v-else-if="isTableTennis"
                                :home-name="homeName" :away-name="awayName"
                                :points-h="currentGamePoints.h" :points-a="currentGamePoints.a"
                                :games-h="homeScore" :games-a="awayScore"
                                :serving-side="servingSide" :games="tennisSets"
                                :action-type="displayAction ? displayAction.type : 0"
                                :action-side="displayAction ? displayAction.side : null"
                                :action-key="displayAction ? displayAction.uniqueKey : ''"
                                :finished="scoreboardFinished"
                                :logo-url="boardLogoUrl" :board-text="$t('live')"
                                :home-color="teamColors.home" :away-color="teamColors.away" />
              <!-- Volleyball: broadcast side view of the court, both teams on
                   it and every point played out (serve, dig, set, spike,
                   block), aces / service errors / time-outs from the stats. -->
              <VolleyArena v-else-if="isVolleyball"
                           :home-name="homeName" :away-name="awayName"
                           :points-h="volleyPoints.h" :points-a="volleyPoints.a"
                           :games-h="homeScore" :games-a="awayScore"
                           :serving-side="servingSide" :games="tennisSets"
                           :aces="volleyStats.aces" :service-errors="volleyStats.serviceErrors" :timeouts="volleyStats.timeouts"
                           :finished="scoreboardFinished"
                           :logo-url="boardLogoUrl" :board-text="$t('live')"
                           :home-color="teamColors.home" :away-color="teamColors.away" />
              <!-- Basketball: broadcast side view of the court (scoreboard,
                   possession, scoring plays). -->
              <BasketArena v-else-if="isBasket"
                           :home-name="homeName" :away-name="awayName"
                           :score-h="homeScore" :score-a="awayScore"
                           :possession-side="possessionSide"
                           :time="displayTime" :period-text="periodLabel"
                           :periods="basketPeriods" :finished="scoreboardFinished"
                           :action-type="displayAction ? displayAction.type : 0"
                           :action-side="displayAction ? displayAction.side : null"
                           :action-key="displayAction ? displayAction.uniqueKey : ''"
                           :logo-url="boardLogoUrl" :board-text="$t('live')"
                           :home-color="teamColors.home" :away-color="teamColors.away" />
              <!-- Darts: front view of the board, every dart of each visit
                   animated, leg/set celebrations. -->
              <DartsArena v-else-if="isDarts"
                          :home-name="homeName" :away-name="awayName"
                          :remaining-h="dartsData.remaining.h" :remaining-a="dartsData.remaining.a"
                          :legs-h="dartsData.legs.h" :legs-a="dartsData.legs.a"
                          :sets-h="dartsData.sets.h" :sets-a="dartsData.sets.a"
                          :set-no="dartsData.setNo" :has-sets="dartsData.hasSets"
                          :turn="servingSide" :finished="scoreboardFinished"
                          :home-color="teamColors.home" :away-color="teamColors.away" />
              <!-- Ice hockey: broadcast view of the rink, the puck moving with
                   possession and incidents, penalties and goal celebrations. -->
              <IceHockeyArena v-else-if="isHockey"
                              :home-name="homeName" :away-name="awayName"
                              :score-h="homeScore" :score-a="awayScore"
                              :possession-side="possessionSide"
                              :time="displayTime" :clock-seconds="localSeconds" :period-text="hockeyPeriodText"
                              :action-type="displayAction ? displayAction.type : 0"
                              :action-side="displayAction ? displayAction.side : null"
                              :action-key="displayAction ? displayAction.uniqueKey : ''"
                              :penalty="hockeyPenalty" :goal-celebration="goalCelebration"
                              :periods="hockeyPeriods" :finished="scoreboardFinished"
                              :logo-url="boardLogoUrl" :board-text="$t('live')"
                              :home-color="teamColors.home" :away-color="teamColors.away"
                              @powerplay-end="onPowerPlayEnd" />
              <!-- Handball: broadcast view of the court, the ball passed
                   around by the team in possession, shots, 7-metre throws,
                   cards / suspensions and goal celebrations. -->
              <HandballArena v-else-if="isHandball"
                             :home-name="homeName" :away-name="awayName"
                             :score-h="homeScore" :score-a="awayScore"
                             :possession-side="possessionSide"
                             :time="displayTime" :clock-seconds="localSeconds" :period-text="handballPeriodText"
                             :action-type="displayAction ? displayAction.type : 0"
                             :action-side="displayAction ? displayAction.side : null"
                             :action-key="displayAction ? displayAction.uniqueKey : ''"
                             :action-text="displayAction ? displayAction.text : ''"
                             :goal-celebration="goalCelebration" :is-break="handballBreak"
                             :periods="handballPeriods" :finished="scoreboardFinished"
                             :logo-url="boardLogoUrl" :board-text="$t('live')"
                             :home-color="teamColors.home" :away-color="teamColors.away" />
              <!-- Aussie rules: broadcast view of the oval, the ball worked
                   forward by the team in possession (kicks, hand balls, marks,
                   tackles), goals / behinds through the posts. -->
              <AussieRulesArena v-else-if="isAussieRules"
                                :home-name="homeName" :away-name="awayName"
                                :score-h="homeScore" :score-a="awayScore"
                                :goals="aflGoals" :behinds="aflBehinds"
                                :possession-side="possessionSide"
                                :time="displayTime" :period-text="aflPeriodText" :period="aflPeriod"
                                :action-type="displayAction ? displayAction.type : 0"
                                :action-side="displayAction ? displayAction.side : null"
                                :action-key="displayAction ? displayAction.uniqueKey : ''"
                                :action-text="displayAction ? displayAction.text : ''"
                                :action-player="displayAction ? displayAction.playerName : ''"
                                :goal-celebration="goalCelebration" :is-break="aflBreak"
                                :break-text="aflPeriodText"
                                :periods="aflPeriods" :finished="scoreboardFinished"
                                :logo-url="boardLogoUrl" :board-text="$t('live')"
                                :home-color="teamColors.home" :away-color="teamColors.away" />
              <!-- Baseball: view from behind home plate — batter / pitcher,
                   count, bases and every play animated on the field. -->
              <BaseballArena v-else-if="isBaseball"
                             :livescore="ls"
                             :home-name="homeName" :away-name="awayName"
                             :home-id="matchData.home_id" :away-id="matchData.away_id"
                             :finished="scoreboardFinished"
                             :logo-url="boardLogoUrl" :board-text="$t('live')"
                             :home-color="teamColors.home" :away-color="teamColors.away" />
              <template v-else>
              <div class="match-progress-bar" v-if="!isTennis">
                <div class="match-progress-fill" :style="{ width: (currentMatchProgressX / 10) + '%' }"></div>
              </div>
              <!-- Stadium bowl behind the pitch: a curved dark shape with a
                   speckled crowd texture, matching a low broadcast camera angle. -->
              <div class="stadium-backdrop">
                <div class="stadium-backdrop__half stadium-backdrop__half--home"></div>
                <div class="stadium-backdrop__half stadium-backdrop__half--away"></div>
              </div>
              <!-- Scrolling pitch-side advertising board. -->
              <div class="ad-board-ticker">
                <!-- Two identical halves: the scroll animation shifts by exactly
                     one half's width, so it wraps into its own duplicate with
                     no visible seam/gap instead of jumping. -->
                <div class="ad-board-track">
                  <span class="ad-board-half">
                    <template v-for="n in 8">
                      <img v-if="boardLogoUrl" :key="'a-img-'+n" :src="boardLogoUrl" class="ad-board-logo" alt="" />
                      <span v-else :key="'a-txt-'+n">{{ $t('live') }}</span>
                    </template>
                  </span>
                  <span class="ad-board-half">
                    <template v-for="n in 8">
                      <img v-if="boardLogoUrl" :key="'b-img-'+n" :src="boardLogoUrl" class="ad-board-logo" alt="" />
                      <span v-else :key="'b-txt-'+n">{{ $t('live') }}</span>
                    </template>
                  </span>
                </div>
              </div>
              <!-- Flat overlay, outside the tilted 3D space on purpose: at a
                   steep rotation the "top" of that space renders heavily
                   compressed (real perspective foreshortening), so a badge
                   placed inside it can end up squeezed against other pitch
                   content near the far edge. -->
              <div class="pitch-header-badge">{{ displayTime }}</div>
              <div class="pitch-frame">
                <!-- Side ad-boards: siblings of the grass in the same tilted
                     3D plane (no counter-rotation), so they recede into the
                     perspective together with the pitch instead of floating
                     flat above it. -->
                <div class="side-ad-board side-ad-board--left">
                  <div class="side-ad-board-track">
                    <span class="side-ad-board-half">
                      <template v-for="n in 10">
                        <img v-if="boardLogoUrl" :key="'sl-img-'+n" :src="boardLogoUrl" class="side-ad-board-logo" alt="" />
                        <span v-else :key="'sl-txt-'+n">{{ $t('live') }}</span>
                      </template>
                    </span>
                    <span class="side-ad-board-half">
                      <template v-for="n in 10">
                        <img v-if="boardLogoUrl" :key="'sl-img2-'+n" :src="boardLogoUrl" class="side-ad-board-logo" alt="" />
                        <span v-else :key="'sl-txt2-'+n">{{ $t('live') }}</span>
                      </template>
                    </span>
                  </div>
                </div>
                <div class="side-ad-board side-ad-board--right">
                  <div class="side-ad-board-track">
                    <span class="side-ad-board-half">
                      <template v-for="n in 10">
                        <img v-if="boardLogoUrl" :key="'sr-img-'+n" :src="boardLogoUrl" class="side-ad-board-logo" alt="" />
                        <span v-else :key="'sr-txt-'+n">{{ $t('live') }}</span>
                      </template>
                    </span>
                    <span class="side-ad-board-half">
                      <template v-for="n in 10">
                        <img v-if="boardLogoUrl" :key="'sr-img2-'+n" :src="boardLogoUrl" class="side-ad-board-logo" alt="" />
                        <span v-else :key="'sr-txt2-'+n">{{ $t('live') }}</span>
                      </template>
                    </span>
                  </div>
                </div>
                <div :class="['pitch-container', sportConfig.class]">
                  <div class="glow-overlay glow-overlay--home" :class="{ 'is-visible': glowClass === 'glow-home' }"></div>
                  <div class="glow-overlay glow-overlay--away" :class="{ 'is-visible': glowClass === 'glow-away' }"></div>
                  <template v-if="sportConfig.lines === 'soccer'">
                    <div class="momentum-arrow" :class="[momentumSide ? 'glow-' + momentumSide : '', { 'is-visible': !!glowClass }]"></div>
                  </template>
                  <div class="pitch-lines">
                    <svg v-if="sportConfig.lines === 'tennis'" width="100%" height="100%" viewBox="0 0 100 65" preserveAspectRatio="none"><g fill="none" stroke="rgba(255,255,255,0.7)" stroke-width="0.5" vector-effect="non-scaling-stroke"><rect x="10" y="5" width="80" height="55" /><line x1="10" y1="32.5" x2="90" y2="32.5" /><line x1="30" y1="5" x2="30" y2="60" /><line x1="70" y1="5" x2="70" y2="60" /></g></svg>
                    <svg v-if="sportConfig.lines === 'soccer'" width="100%" height="100%" viewBox="0 0 100 65" preserveAspectRatio="none"><g fill="none" stroke="rgba(255,255,255,0.85)" stroke-width="0.5" vector-effect="non-scaling-stroke"><rect x="0.3" y="0.3" width="99.4" height="64.4" /><line x1="50" y1="0" x2="50" y2="65" /><circle cx="50" cy="32.5" r="9" /><circle cx="50" cy="32.5" r="0.5" fill="white" /><rect x="0" y="14" width="16" height="37" /><rect x="0" y="24" width="6" height="17" /><path d="M 16,25 A 9,9 0 0 1 16,40" /><circle cx="11" cy="32.5" r="0.5" fill="white" /><rect x="84" y="14" width="16" height="37" /><rect x="94" y="24" width="6" height="17" /><path d="M 84,25 A 9,9 0 0 0 84,40" /><circle cx="89" cy="32.5" r="0.5" fill="white" /><path d="M 0,2 A 2,2 0 0 0 2,0" /><path d="M 98,0 A 2,2 0 0 0 100,2" /><path d="M 0,63 A 2,2 0 0 1 2,65" /><path d="M 100,63 A 2,2 0 0 0 98,65" /></g></svg>
                    <svg v-if="sportConfig.lines === 'basket'" width="100%" height="100%" viewBox="0 0 100 65" preserveAspectRatio="none"><g fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="0.5" vector-effect="non-scaling-stroke"><rect x="0" y="0" width="100" height="65" stroke-opacity="0.2" /><circle cx="50" cy="32.5" r="12" /><line x1="50" y1="0" x2="50" y2="65" /><path d="M0,15 Q20,32.5 0,50" /><path d="M100,15 Q80,32.5 100,50" /><rect x="0" y="25" width="8" height="15" /><rect x="92" y="25" width="8" height="15" /></g></svg>
                    <svg v-if="sportConfig.lines === 'hockey'" width="100%" height="100%" viewBox="0 0 100 65" preserveAspectRatio="none"><g fill="none" stroke-width="0.6" vector-effect="non-scaling-stroke"><rect x="0" y="0" width="100" height="65" rx="15" stroke="rgba(255,255,255,0.3)" /><line x1="33" y1="0" x2="33" y2="65" stroke="#38bdf8" stroke-width="0.8"/><line x1="67" y1="0" x2="67" y2="65" stroke="#38bdf8" stroke-width="0.8"/><line x1="50" y1="0" x2="50" y2="65" stroke="#ef4444" stroke-width="1" stroke-dasharray="2,1" /><circle cx="50" cy="32.5" r="5" stroke="#ef4444" /><line x1="10" y1="2" x2="10" y2="63" stroke="#ef4444" /><line x1="90" y1="2" x2="90" y2="63" stroke="#ef4444" /><path d="M10,27.5 L13,27.5 Q15,32.5 13,37.5 L10,37.5" stroke="#ef4444" fill="rgba(56, 189, 248, 0.2)" /><path d="M90,27.5 L87,27.5 Q85,32.5 87,37.5 L90,37.5" stroke="#ef4444" fill="rgba(56, 189, 248, 0.2)" /><circle cx="22" cy="18" r="8" stroke="#ef4444" opacity="0.5" /><circle cx="22" cy="47" r="8" stroke="#ef4444" opacity="0.5" /><circle cx="78" cy="18" r="8" stroke="#ef4444" opacity="0.5" /><circle cx="78" cy="47" r="8" stroke="#ef4444" opacity="0.5" /><circle cx="22" cy="18" r="0.5" fill="#ef4444" /><circle cx="22" cy="47" r="0.5" fill="#ef4444" /><circle cx="78" cy="18" r="0.5" fill="#ef4444" /><circle cx="78" cy="47" r="0.5" fill="#ef4444" /><circle cx="50" cy="32.5" r="0.5" fill="#ef4444" /></g></svg>
                    <svg v-if="sportConfig.lines === 'cricket'" width="100%" height="100%" viewBox="0 0 100 65" preserveAspectRatio="none"><g fill="none" stroke="white" stroke-opacity="0.4" stroke-width="0.5" vector-effect="non-scaling-stroke"><ellipse cx="50" cy="32.5" rx="45" ry="30" /><rect x="44" y="25" width="12" height="15" fill="rgba(255,255,255,0.1)" /></g></svg>
                    <svg v-if="sportConfig.lines === 'volley' || isVolley" width="100%" height="100%" viewBox="0 0 100 65" preserveAspectRatio="none"><g fill="none" stroke="rgba(255,255,255,0.7)" stroke-width="0.5" vector-effect="non-scaling-stroke"><rect x="5" y="5" width="90" height="55" /><line x1="50" y1="5" x2="50" y2="60" stroke-width="1" stroke-opacity="1"/><line x1="33" y1="5" x2="33" y2="60" stroke-dasharray="2,2" /><line x1="67" y1="5" x2="67" y2="60" stroke-dasharray="2,2" /><line x1="5" y1="5" x2="2" y2="5" /><line x1="5" y1="60" x2="2" y2="60" /><line x1="95" y1="5" x2="98" y2="5" /><line x1="95" y1="60" x2="98" y2="60" /></g></svg>
                    <svg v-if="sportConfig.lines === 'futsal'" width="100%" height="100%" viewBox="0 0 100 65" preserveAspectRatio="none"><g fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="0.6" vector-effect="non-scaling-stroke"><rect x="5" y="5" width="90" height="55" /><line x1="50" y1="5" x2="50" y2="60" /><circle cx="50" cy="32.5" r="7" /> <path d="M 5,17.5 A 15,15 0 0 1 20,32.5 A 15,15 0 0 1 5,47.5" /><circle cx="20" cy="32.5" r="0.5" fill="white" /><line x1="28" y1="31.5" x2="28" y2="33.5" /><path d="M 95,17.5 A 15,15 0 0 0 80,32.5 A 15,15 0 0 0 95,47.5" /><circle cx="80" cy="32.5" r="0.5" fill="white" /><line x1="72" y1="31.5" x2="72" y2="33.5" /><path d="M 5,7.5 A 2.5,2.5 0 0 0 7.5,5" /><path d="M 5,57.5 A 2.5,2.5 0 0 1 7.5,60" /><path d="M 95,7.5 A 2.5,2.5 0 0 1 92.5,5" /><path d="M 95,57.5 A 2.5,2.5 0 0 0 92.5,60" /></g></svg>
                  </div>
                </div>
                <div class="holo-ball" :style="{ left: ballPosition.x + '%', top: ballPosition.y + '%', opacity: ballPosition.opacity }">
                  <div class="holo-ball-shadow"></div>
                  <span class="ball-dot"></span>
                </div>
              </div>
              </template>
              <!-- Before kick-off: tournament, time, teams and form over the pitch. -->
              <PreMatchBoard v-if="isPreMatch" :match-data="matchData" :home-name="homeName" :away-name="awayName" />
              <!-- Incident strip: a dedicated full-width bar below the pitch,
                   outside the tilted 3D space, instead of a label floating
                   over the grass — no more clamping its position to keep it
                   from clipping past the card's edge at narrow widths. -->
              <div v-if="!isTennis && !isTableTennis && !isVolleyball && !isBasket && !isDarts && !isBaseball" class="incident-strip" :class="{ 'is-visible': !!displayAction, 'is-pitch': isSoccerStadium, 'is-rink': isHockey, 'is-court': isHandball, 'is-oval': isAussieRules }"
                   @mouseenter="displayAction && showTooltip($event, displayAction.fullTooltip)" @mousemove="updateTooltipPos" @mouseleave="hideTooltip">
                <transition name="ar-content-fade" mode="out-in">
                  <div v-if="displayAction" :key="displayAction.uniqueKey" class="incident-marker" :class="{ 'is-goal': displayAction.isGoal, 'is-away': displayAction.side === '2' }">
<!--                    <span class="incident-dot" :style="{ background: displayAction.color, boxShadow: `0 0 6px ${displayAction.color}` }"></span>-->
                    <span class="incident-bar" :style="{ background: incidentSideColor }"></span>
                    <span class="incident-text">
                      <span class="incident-sub">{{ displayAction.teamName }}<template v-if="displayAction.playerName"> · <span v-html="displayAction.playerName"></span></template></span>
                      <span class="incident-title">{{ displayAction.text }}<template v-if="displayAction.isGoal">!</template></span>
                    </span>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="stage-secondary-content" v-show="mainViewTab !== 'live' && !isTennis && !isTableTennis && !isVolleyball">
        <div class="stage-right-inner">
          <MatchTimeline v-if="mainViewTab === 'timeline'" :match-data="matchData" :incidents="allIncidents" />
          <div class="component-glass-wrapper" v-if="mainViewTab === 'stats'">
            <StatsSection :event="matchData" />
          </div>
          <div class="component-glass-wrapper" v-if="mainViewTab === 'lineups'">
            <LineupSection :match-data="matchData" :event-id="eventId" :initial-sport-id="initialSportId"/>
          </div>
        </div>
      </div>

    </div>

<!--    <div class="floating-dock-wrapper" v-if="!isTennis">-->
<!--      <div class="floating-dock">-->
<!--        <button class="custom-dock-btn" :class="{ 'is-active': mainViewTab === 'live' }" @click="changeTab('live')" aria-label="Match">-->
<!--          <v-icon class="dock-icon">mdi-soccer-field</v-icon>-->
<!--          <span class="dock-active-indicator"></span>-->
<!--        </button>-->
<!--        <button class="custom-dock-btn" :class="{ 'is-active': mainViewTab === 'stats' }" @click="changeTab('stats')" aria-label="Stats">-->
<!--          <v-icon class="dock-icon">mdi-chart-bar</v-icon>-->
<!--          <span class="dock-active-indicator"></span>-->
<!--        </button>-->
<!--        <button class="custom-dock-btn" :class="{ 'is-active': mainViewTab === 'timeline' }" @click="changeTab('timeline')" aria-label="Timeline">-->
<!--          <v-icon class="dock-icon">mdi-format-list-bulleted</v-icon>-->
<!--          <span class="dock-active-indicator"></span>-->
<!--        </button>-->
<!--        <button class="custom-dock-btn" :class="{ 'is-active': mainViewTab === 'lineups' }" @click="changeTab('lineups')" aria-label="Lineups">-->
<!--          <v-icon class="dock-icon">mdi-account-group</v-icon>-->
<!--          <span class="dock-active-indicator"></span>-->
<!--        </button>-->
<!--      </div>-->
<!--    </div>-->
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MomentumStats from "@/components/center_widgets/Live/TradeTracker/MomentumStats.vue";
import MatchToolbar from "@/components/center_widgets/Live/TradeTracker/MatchToolbar.vue";
import StatsSection from "@/components/center_widgets/Live/TradeTracker/StatsSection.vue";
import LineupSection from "@/components/center_widgets/Live/TradeTracker/LineUpSection.vue";
import MatchTimeline from "@/components/center_widgets/Live/TradeTracker/MatchTimeline.vue";
import PreMatchBoard from "@/components/arenamatchtracker/PreMatchBoard.vue";
import SoccerStadium from "@/components/arenamatchtracker/SoccerStadium.vue";
import {BOARD_LOGO} from "@/services/matchtracker/broadcastScene";
import {resolveBoardLogo} from "@/services/matchtracker/boardLogo";
import {FOOTBALL_INCIDENTS, FOOTBALL_STATS_TO_TRACK, resolveFootballType, footballIncidentKey} from "@/services/matchtracker/footballIncidents";
import TennisStadium from "@/components/arenamatchtracker/TennisStadium.vue";
import TableTennisArena from "@/components/arenamatchtracker/TableTennisArena.vue";
import BasketArena from "@/components/arenamatchtracker/BasketArena.vue";
import DartsArena from "@/components/arenamatchtracker/DartsArena.vue";
import IceHockeyArena, {POWERPLAY_END_TYPE} from "@/components/arenamatchtracker/IceHockeyArena.vue";
import BaseballArena from "@/components/arenamatchtracker/BaseballArena.vue";
import HandballArena from "@/components/arenamatchtracker/HandballArena.vue";
import {HANDBALL_SPORT_ID, HANDBALL_STATS_TO_TRACK, HANDBALL_GOAL_KINDS, resolveHandballType, handballIncidentKey} from "@/services/matchtracker/handballIncidents";
import VolleyArena from "@/components/arenamatchtracker/VolleyArena.vue";
import AussieRulesArena from "@/components/arenamatchtracker/AussieRulesArena.vue";
import {AFL_SPORT_ID, AFL_STATS_TO_TRACK, AFL_PRIORITY, AFL_BEHIND_TYPE, AFL_GOALS_STAT, resolveAflType, aflIncidentKey} from "@/services/matchtracker/aflIncidents";
import {FULL_MAPPING, PERIODS_MAP} from "@/services/matchtracker/statNames";
import {resolveTeamColors, contrastText} from "@/services/matchtracker/teamColors";
import {buildBreakSummary} from "@/services/matchtracker/breakSummary";
import _ from 'lodash';
import {isMobileOnly} from "mobile-device-detect";

// Which incident wins when several arrive together (higher = shown).
const POPUP_PRIORITY={2174:88,2036:81,2035:40,2038:38,31:60,2282:82,2037:80,2393:33,2048:8,33:6,11:1,9:100,24:100,25:100,7:90,1074:85,8:80,2389:79,40:78,6:70,2:60,18:58,3:55,16:54,96:52,15:50,1:45,2394:42,13:40,2148:36,17:35,14:34,88:31,12:30,2474:26,10:25,2158:25,2440:18,2439:17,2438:16,5:15,2430:12,2228:11,2115:11,4:10,2046:5};
const popupType = inc => parseInt(inc.IncidentType || inc.incidentType || inc.id || 0);
const popupPriority = inc => POPUP_PRIORITY[popupType(inc)] ?? 20;
const GOAL_TYPES = [9, 24, 25];
const VAR_TYPE = 1074;
const POSSESSION_TYPE = 11;
// VAR review subjects, as the feed's "VAR" extra data names them.
const VAR_REASON_I18N = { penalty: 'incidents.penalty', goal: 'incidents.goal', redcard: 'incidents.redcard', offside: 'incidents.offside', yellowcard: 'incidents.yellowcard' };
// Ice hockey (LSports incident list): stats whose increase is announced,
// incidents taken from the feed's CurrentIncident, and their labels. Goals
// come from the scoreboard (27 Score in the feed is read as a goal).
const HOCKEY_SPORT_ID = '35232';
// Baseball: BaseballArena reads the livescore itself (count, bases, plays).
const BASEBALL_SPORT_ID = '154914';
const VOLLEYBALL_SPORT_ID = '154830';
const HOCKEY_STATS_TO_TRACK = [2, 3, 4, 8, 15, 16, 33, 40, 96, 2037, 2048, 2282, 2393, 2472];
const HOCKEY_FEED_TYPES = [2, 3, 4, 8, 15, 16, 25, 27, 33, 40, 96, 1074, 2037, 2048, 2282, 2393, 2438, 2439, 2472, 2494];
// Feed/stat types that mean the same as another one here.
const HOCKEY_ALIASES = { 27: 9, 2494: 2048, 2472: VAR_TYPE };
const HOCKEY_BREAK_TYPE = 2423;
const HOCKEY_PENALTY_MINUTES = { 2037: 2, 2282: 5 };
// Stats that go up together with a goal and say what kind of goal it was.
const HOCKEY_GOAL_KINDS = { 25: 'hockey.penaltyshotgoal', 2047: 'hockey.powerplaygoal', 2441: 'hockey.powerplaygoal', 2177: 'hockey.shorthandedgoal', 2249: 'hockey.emptynetgoal' };
const HOCKEY_I18N = { [POWERPLAY_END_TYPE]: 'hockey.powerplayend', 2: 'hockey.shotongoal', 3: 'hockey.shotwide', 4: 'hockey.attack', 8: 'hockey.penaltyshot', 9: 'hockey.goal', 11: 'hockey.inpossession', 15: 'hockey.offside', 16: 'hockey.blockedshot', 25: 'hockey.penaltyshotgoal', 33: 'hockey.hit', 40: 'hockey.missedpenaltyshot', 96: 'hockey.save', 1074: 'hockey.videoreview', 2037: 'hockey.penalty', 2048: 'hockey.faceoff', 2282: 'hockey.penalty', 2393: 'hockey.icing', 2438: 'hockey.dangerousattack', 2439: 'hockey.possiblepenalty' };
// A hockey goal stays on screen this long: faceoffs and shots arriving
// right after it don't cut its celebration short.
const HOCKEY_GOAL_HOLD_MS = 9000;
// Handball: goals come every minute or so, the celebration is shorter. Cards,
// suspensions, 7-metre throws and time-outs still get through.
const HANDBALL_GOAL_HOLD_MS = 6500;
const HANDBALL_HOLD_EXEMPT = [6, 7, 31, 2036, 2037, 2174];
// Aussie rules: after a goal comes the centre bounce, nothing gets in its way.
const AFL_GOAL_HOLD_MS = 7000;
// A real incident (shot, corner, ...) stays at least this long before a
// plain possession reading may replace it.
const POSSESSION_HOLD_MS = 6000;
// Incidents that can be shown without a team side (VAR reviews concern the
// match, not one team).
const NEUTRAL_OK = inc => popupType(inc) === VAR_TYPE || [9001, 9002, 9003, 9004].includes(popupType(inc));
const PITCH_BADGE_TYPES=[12,16,18,88,96,2046,2115,2148,2228,2394,2430,2438,2439,2440];
// Incidents that get the big on-pitch badge (icon + name + team) instead.
// Match-phase pseudo-incidents (not LSports types): shown like incidents,
// with no team side. Half time / full time are derived from the scoreboard's
// CurrentPeriod (80 = break, 31 = half time, 100+ = finished).
// Default home/away colours when a team has no shirt colour.
const PHASE = { HALFTIME: 9001, SECOND_HALF: 9002, FULLTIME: 9003, KICKOFF: 9004 };
const PHASE_TYPES = Object.values(PHASE);
const PHASE_I18N = { 9001: 'incidents.halftime', 9002: 'incidents.secondhalfstart', 9003: 'incidents.fulltime', 9004: 'incidents.kickoff' };
const BIG_BADGE_KINDS={2:'on-target',3:'off-target',6:'yellow',7:'red',8:'penalty',40:'missed-penalty',9:'goal',10:'substitution',15:'offside',24:'goal',25:'goal',1074:'var',2158:'injury',2389:'missed-penalty',2474:'injury',9001:'whistle',9002:'whistle',9003:'whistle',9004:'whistle'};
const TRACKER_STYLES={1:{category:'center',icon:'🚩',color:'#fff',shadow:'rgba(255,255,255,0.4)'},2:{category:'directional',icon:'🎯',color:'#10b981',shadow:'rgba(16,185,129,0.3)'},3:{category:'directional',icon:'👟',color:'#94a3b8',shadow:'rgba(148,163,184,0.3)'},4:{category:'directional',icon:'➤',color:'#0ea5e9',shadow:'rgba(14,165,233,0.4)'},5:{category:'directional',icon:'🔥',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'},6:{category:'directional',icon:'🟨',color:'#facc15',shadow:'rgba(250,204,21,0.5)'},7:{category:'directional',icon:'🟥',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'},8:{category:'center',icon:'⚽',color:'#f59e0b',shadow:'rgba(245,158,11,0.5)'},9:{category:'center',icon:'⚽',color:'#22c55e',shadow:'rgba(34,197,94,0.5)'},10:{category:'center',icon:'🔄',color:'#8b5cf6',shadow:'rgba(139,92,246,0.4)'},12:{category:'center',icon:'⚠️',color:'#94a3b8',shadow:'rgba(0,0,0,0.5)'},13:{category:'directional',icon:'🦶',color:'#3b82f6',shadow:'rgba(59,130,246,0.4)'},14:{category:'center',icon:'👟',color:'#94a3b8',shadow:'rgba(0,0,0,0.4)'},15:{category:'directional',icon:'🏁',color:'#facc15',shadow:'rgba(250,204,21,0.3)'},16:{category:'center',icon:'🛡️',color:'#94a3b8',shadow:'rgba(0,0,0,0.4)'},17:{category:'directional',icon:'👐',color:'#94a3b8',shadow:'rgba(0,0,0,0.4)'},18:{category:'center',icon:'💥',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'},20:{category:'center',icon:'🎾',color:'#8bc34a',shadow:'rgba(139,195,74,0.5)'},21:{category:'center',icon:'❌',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'},22:{category:'center',icon:'🎾',color:'#0ea5e9',shadow:'rgba(14,165,233,0.4)'},23:{category:'center',icon:'💎',color:'#06b6d4',shadow:'rgba(6,182,212,0.4)'},24:{category:'center',icon:'⚠️',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'},25:{category:'center',icon:'🥅',color:'#22c55e',shadow:'rgba(34,197,94,0.5)'},27:{category:'center',icon:'🏅',color:'#d9f99d',shadow:'rgba(217,249,157,0.5)'},28:{category:'center',icon:'🏀',color:'#22c55e',shadow:'rgba(34,197,94,0.5)'},30:{category:'center',icon:'🏀',color:'#22c55e',shadow:'rgba(34,197,94,0.5)'},31:{category:'center',icon:'⏱️',color:'#94a3b8',shadow:'rgba(0,0,0,0.4)'},32:{category:'center',icon:'🏀',color:'#f59e0b',shadow:'rgba(245,158,11,0.5)'},40:{category:'center',icon:'❌⚽',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'},88:{category:'center',icon:'🛡️',color:'#94a3b8',shadow:'rgba(0,0,0,0.4)'},96:{category:'center',icon:'🧤',color:'#0ea5e9',shadow:'rgba(14,165,233,0.4)'},129:{category:'center',icon:'🏈',color:'#22c55e',shadow:'rgba(34,197,94,0.5)'},1005:{category:'center',icon:'🐉',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'},1074:{category:'center',icon:'🖥️',color:'#06b6d4',shadow:'rgba(6,182,212,0.5)'},1080:{category:'center',icon:'🏉',color:'#22c55e',shadow:'rgba(34,197,94,0.5)'},2019:{category:'center',icon:'⚔️',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'},2158:{category:'directional',icon:'🚑',color:'#e11d48',shadow:'rgba(225,29,72,0.4)'}};

export default {
  name: "MatchTracker",
  components: { MomentumStats, MatchToolbar, StatsSection, LineupSection, MatchTimeline, PreMatchBoard, SoccerStadium, TennisStadium, TableTennisArena, BasketArena, DartsArena, IceHockeyArena, HandballArena, BaseballArena, VolleyArena, AussieRulesArena },
  props: {
    matchData: { type: Object, required: true },
    eventId: { type: [String, Number], required: true },
    initialSportId: { type: [String, Number], required: true },
    // live-scores/player-stats answer (MatchCenter polls it), for the
    // half-time summary.
    playerStats: { type: Object, default: null },
    isExpanded: { type: Boolean, default: false },
    // False when the page keeps the size fixed: no fullscreen button.
    resizable: { type: Boolean, default: true },
    // False where the page shows its own scoreboard (American module).
    showMomentum: { type: Boolean, default: true }
  },
  data() {
    return {
      isMobileOnly: isMobileOnly,
      mainViewTab: 'live',
      polling: null,
      timerId: null,
      localSeconds: 0,
      liveIncidentsHistory: [],
      lastStats: {},
      lastHomeScore: 0,
      lastAwayScore: 0,
      isInitialLoad: true,
      specialEvent: null,
      lastIncidentId: null,
      lastPeriod: null,
      // BreakTime (2423) named by the feed as CurrentIncident.
      feedBreak: false,
      // Last VAR value read from LivescoreExtraData (null = no review).
      lastVarValue: null,
      // lastUpdate (ms) of the livescore message behind the incident on
      // screen, and of the message being processed right now.
      lastIncidentUpdateMs: 0,
      currentMessageUpdateMs: 0,
      // Last goal read from the scoreboard: { side, fromHome, fromAway, at }.
      recentScoreChange: null,
      lastServerTime: null,
      activeTab: 0,
      statNames: FULL_MAPPING,
      currentStatPage: 0,
      statCarouselInterval: null,
      tooltip: { visible: false, text: '', x: 0, y: 0 },
      lastGamePointsH: "0",
      lastGamePointsA: "0",
      momentumSide: null
    };
  },
  watch: {
    matchData: {
      handler(newData) {
        this.setMatchData(newData);
      },
      deep: true,
      immediate: true
    },
    // Sticky side for the momentum cone/glow: only updates on a definite
    // home/away reading, so it keeps its last shape+colour (just fading out)
    // instead of resetting whenever possession briefly reads as unclear.
    eventId() {
      this.specialEvent = null;
      this.lastIncidentUpdateMs = 0;
    },
    glowClass(val) {
      if (val === 'glow-home') this.momentumSide = 'home';
      else if (val === 'glow-away') this.momentumSide = 'away';
    },
  },
  mounted() {
    this.statCarouselInterval = setInterval(() => {
      if (this.paginatedStats && this.paginatedStats.length > 1) {
        this.currentStatPage = (this.currentStatPage + 1) % this.paginatedStats.length;
      }
    }, 6000);
  },
  beforeDestroy() {
    clearInterval(this.statCarouselInterval);
    this.stopClock();
  },
  methods: {
    async setMatchData(newData) {
      const ls = newData?.Livescore || newData?.livescore || newData;
      if (!ls) return;
      // One feed update can carry several candidates at once (the feed's own
      // CurrentIncident, plus every stat that went up: a shot off target also
      // bumps total shots, a goal also bumps shots on target). Collect them
      // and show exactly one — see the pick below.
      const popups = [];
      let phaseEvent = null;
      const sb = ls.Scoreboard || ls.scoreboard;
      const sportId = String(newData?.sportId || "");
      const isVolley = ["154830", "621569", "1149113", "1149117"].includes(sportId);
      const extra = ls.LivescoreExtraData || ls.livescoreExtraData || [];
      const turnObj = extra.find(x => String(x.Name || x.name).toLowerCase() === "turn");
      const currentServer = turnObj ? String(turnObj.Value || turnObj.value) : null;
      if (sb) {
        const clock = sb.Clock || sb.clock;
        let serverTime = parseInt(sb.Time || sb.time || clock?.Seconds || clock?.seconds || 0);
        // Aussie rules: Time is always -1, the clock has the time left in
        // the quarter.
        if (this.isAussieRules && serverTime < 0) serverTime = parseInt(clock?.Seconds ?? clock?.seconds ?? 0) || 0;
        if (!isNaN(serverTime) && serverTime !== this.lastServerTime) {
          this.localSeconds = serverTime;
          this.lastServerTime = serverTime;
        }
        // The clock only runs while the ball is in play: not during the
        // break, after the final whistle, or while the feed says it's stopped.
        const period = parseInt(sb.CurrentPeriod ?? sb.currentPeriod ?? 0);
        // Aussie rules: the clock runs only with status 1 (0 = stopped / not started).
        const clockStatus = parseInt(clock?.Status ?? clock?.status ?? 1);
        const clockStopped = this.isAussieRules ? clockStatus !== 1 : clockStatus === 2;
        const isBreak = this.isAussieRules ? this.aflBreak : (period === 31 || period >= 80);
        if (parseInt(sb.Status || sb.status) === 2 && !isBreak && !clockStopped) { this.startClock(); }
        else { this.stopClock(); }
        // Kick-off / second-half kick-off, announced like an incident.
        if (!this.isInitialLoad && this.lastPeriod !== null && period !== this.lastPeriod) {
          if (period === 20) phaseEvent = { IncidentType: PHASE.SECOND_HALF, ParticipantPosition: '0' };
          else if (period === 10 && (this.lastPeriod <= 0 || this.lastPeriod === 99)) phaseEvent = { IncidentType: PHASE.KICKOFF, ParticipantPosition: '0' };
        }
        if (this.lastPeriod !== null && period !== this.lastPeriod) this.feedBreak = false;
        this.lastPeriod = period;
        const res = sb?.Results || sb?.results || [];
        const hR = res.find(x => String(x.Position || x.position) == "1");
        const aR = res.find(x => String(x.Position || x.position) == "2");
        // The socket sends camelCase (`value`); reading only `Value` left the
        // score at 0 forever, so goals were never detected from it. A side
        // missing from this message keeps its last score (no fake 0 -> N goal).
        const readScore = (r, last) => {
          const v = parseInt(r?.Value ?? r?.value);
          return isNaN(v) ? last : v;
        };
        const cHomeScore = readScore(hR, this.lastHomeScore);
        const cAwayScore = readScore(aR, this.lastAwayScore);
        if (!this.isInitialLoad) {
          // Aussie rules: a goal is 6 points, less than that is a behind.
          const scoreType = diff => (this.isAussieRules && diff < 6 ? AFL_BEHIND_TYPE : 9);
          if (cHomeScore > this.lastHomeScore) {
            const type = scoreType(cHomeScore - this.lastHomeScore);
            popups.push({ IncidentType: type, ParticipantPosition: "1", source: 'score' });
            if (type === 9) this.recentScoreChange = { side: '1', fromHome: this.lastHomeScore, fromAway: this.lastAwayScore, at: Date.now() };
          }
          if (cAwayScore > this.lastAwayScore) {
            const type = scoreType(cAwayScore - this.lastAwayScore);
            popups.push({ IncidentType: type, ParticipantPosition: "2", source: 'score' });
            if (type === 9) this.recentScoreChange = { side: '2', fromHome: this.lastHomeScore, fromAway: this.lastAwayScore, at: Date.now() };
          }
        }
        // Football: a goal read from the scoreboard also goes into the match
        // history, so the timeline lists it even while the feed has no goal
        // incident for it yet (MatchTimeline drops it once the real one comes).
        if (!this.isInitialLoad && ['soccer', 'futsal'].includes(this.sportConfig.lines)) {
          [['1', cHomeScore, this.lastHomeScore], ['2', cAwayScore, this.lastAwayScore]].forEach(([side, curr, last]) => {
            for (let n = last + 1; n <= curr; n++) {
              const uniqueId = `score-${side}-${n}`;
              if (!this.liveIncidentsHistory.find(x => x.uniqueId === uniqueId)) {
                this.liveIncidentsHistory.push({ IncidentType: 9, ParticipantPosition: side, Seconds: this.localSeconds, uniqueId, fromScore: true });
              }
            }
          });
        }
        this.lastHomeScore = cHomeScore;
        this.lastAwayScore = cAwayScore;
        if (this.isTennis || isVolley) {
          const gp = isVolley ? this.getVolleySetPoints() : this.currentGamePoints;
          if (!this.isInitialLoad) {
            const hChanged = gp.h !== this.lastGamePointsH && gp.h !== "0";
            const aChanged = gp.a !== this.lastGamePointsA && gp.a !== "0";
            if (hChanged || aChanged) {
              const side = hChanged ? "1" : "2";
              const latestInc = ls.CurrentIncident || ls.currentIncident;
              const isAce = latestInc && parseInt(latestInc.IncidentType || latestInc.incidentType) === 20;
              popups.push({
                IncidentType: isAce ? 20 : 27,
                ParticipantPosition: side,
                Server: currentServer
              });
            }
          }
          this.lastGamePointsH = gp.h;
          this.lastGamePointsA = gp.a;
        }
      }
      const isFootball = ['soccer', 'futsal'].includes(this.sportConfig.lines);
      const isHockey = this.isHockey;
      const isHandball = this.isHandball;
      const isAfl = this.isAussieRules;
      const statsToTrack = isFootball
        ? FOOTBALL_STATS_TO_TRACK
        : isHandball ? HANDBALL_STATS_TO_TRACK
        : isAfl ? AFL_STATS_TO_TRACK
        : isHockey ? [...HOCKEY_STATS_TO_TRACK, ...Object.keys(HOCKEY_GOAL_KINDS).map(Number)] : [1, 2, 3, 5, 6, 7, 8, 10, 12, 13, 14, 15, 16, 17, 18, 20, 21, 23, 24, 25, 27, 28, 30, 31, 32, 40, 88, 96, 1074, 2046, 2158];
      statsToTrack.forEach(type => {
        const currH = parseInt(this.getStat(type, '1') || 0);
        const currA = parseInt(this.getStat(type, '2') || 0);
        const lastH = this.lastStats[`${type}-1`] || 0;
        const lastA = this.lastStats[`${type}-2`] || 0;
        if (!this.isInitialLoad && (currH > lastH || currA > lastA)) {
          popups.push({
            IncidentType: type,
            ParticipantPosition: currH > lastH ? "1" : "2",
            Seconds: this.localSeconds
          });
        }
        this.lastStats[`${type}-1`] = currH;
        this.lastStats[`${type}-2`] = currA;
      });
      // VAR: besides incident 1074, the feed flags a review in progress with a
      // "VAR" entry in LivescoreExtraData (non-empty value while it lasts) —
      // the same signal the live lists use for their VAR badge.
      if (isFootball) {
        const varEntry = extra.find(e => e && String(e.Name || e.name || '').toUpperCase() === 'VAR');
        const rawVar = varEntry ? (varEntry.Value ?? varEntry.value) : null;
        const varValue = rawVar !== null && rawVar !== undefined && String(rawVar) !== '' && String(rawVar) !== '0' && String(rawVar).toLowerCase() !== 'false' ? String(rawVar) : null;
        if (varValue !== this.lastVarValue) {
          if (!this.isInitialLoad && varValue !== null && this.lastVarValue === null) {
            popups.push({ IncidentType: VAR_TYPE, ParticipantPosition: '0', source: 'var', varText: isNaN(Number(varValue)) ? varValue : '' });
          }
          this.lastVarValue = varValue;
        }
      }
      let latestInc = ls.CurrentIncident || ls.currentIncident;
      if (latestInc) {
        const type = parseInt(latestInc.IncidentType || latestInc.id || 0);
        const uniqueId = latestInc.Id || `${type}-${latestInc.Seconds}`;
        if (type !== 0 && !this.liveIncidentsHistory.find(x => x.uniqueId === uniqueId)) {
          this.liveIncidentsHistory.push({ ...latestInc, uniqueId });
        }
        if (isFootball) {
          if (!this.isInitialLoad && uniqueId !== this.lastIncidentId) {
            this.lastIncidentId = uniqueId;
            const r = resolveFootballType(type);
            if (r.breakTime) {
              this.feedBreak = true;
            } else if (r.ignored) {
              // stat / meta record, not a pitch incident
            } else {
              this.feedBreak = false;
              // Side: from the incident itself, else from a stat of the same
              // type that went up in this update, else from possession.
              let side = String(latestInc.ParticipantPosition ?? latestInc.participantPosition ?? latestInc.position ?? latestInc.Position ?? '');
              if (side !== '1' && side !== '2') {
                const sameStat = popups.find(p => popupType(p) === r.type);
                side = sameStat ? sameStat.ParticipantPosition : (this.possessionSide || '');
              }
              popups.push({ ...latestInc, IncidentType: r.type, originalType: type, ParticipantPosition: side, source: 'feed' });
            }
          }
        } else if (isHandball || isAfl) {
          if (!this.isInitialLoad && uniqueId !== this.lastIncidentId) {
            this.lastIncidentId = uniqueId;
            const r = isAfl ? resolveAflType(type) : resolveHandballType(type);
            const incidentKey = isAfl ? aflIncidentKey : handballIncidentKey;
            if (r.breakTime) {
              this.feedBreak = true;
            } else if (!r.ignored && incidentKey(r.type)) {
              this.feedBreak = false;
              // Side: from the incident, else from a stat of the same type
              // that went up in this update, else from possession.
              let side = String(latestInc.ParticipantPosition ?? latestInc.participantPosition ?? '');
              if (side !== '1' && side !== '2') {
                const sameStat = popups.find(p => popupType(p) === r.type);
                side = sameStat ? sameStat.ParticipantPosition : (this.possessionSide || '');
              }
              popups.push({ ...latestInc, IncidentType: r.type, originalType: type, ParticipantPosition: side, source: 'feed' });
            }
          }
        } else if (isHockey) {
          if (!this.isInitialLoad && uniqueId !== this.lastIncidentId && type === HOCKEY_BREAK_TYPE) {
            this.lastIncidentId = uniqueId;
            this.feedBreak = true;
          } else if (!this.isInitialLoad && HOCKEY_FEED_TYPES.includes(type) && uniqueId !== this.lastIncidentId) {
            this.lastIncidentId = uniqueId;
            this.feedBreak = false;
            const t = HOCKEY_ALIASES[type] || type;
            // Side: from the incident, else from a stat of the same type
            // that went up in this update, else from possession (a video
            // review can stay without one).
            let side = String(latestInc.ParticipantPosition ?? latestInc.participantPosition ?? '');
            if (side !== '1' && side !== '2') {
              const sameStat = popups.find(p => popupType(p) === t);
              side = sameStat ? sameStat.ParticipantPosition : (t === VAR_TYPE ? '0' : (this.possessionSide || ''));
            }
            popups.push({ ...latestInc, IncidentType: t, originalType: type, ParticipantPosition: side, source: 'feed' });
          }
        } else {
          const popupEvents = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 13, 14, 15, 16, 17, 18, 20, 21, 23, 24, 25, 27, 28, 30, 31, 32, 40, 96, 1074, 2158];
          if (!this.isInitialLoad && popupEvents.includes(type) && uniqueId !== this.lastIncidentId) {
            this.lastIncidentId = uniqueId;
            popups.push({ ...latestInc, source: 'feed' });
          }
        }
      }
      if (isHockey) this.resolveGoalKindPopups(popups, HOCKEY_GOAL_KINDS, HOCKEY_ALIASES);
      if (isHandball) this.resolveGoalKindPopups(popups, HANDBALL_GOAL_KINDS);
      // Message order: an incident is announced only from a livescore
      // message newer than the one behind the incident already on screen. A
      // late message can describe something that happened before it.
      // (Messages without a timestamp can't be judged, so they pass.)
      const msgUpdate = parseInt(ls.lastMessageUpdate ?? ls.LastMessageUpdate ?? 0) || 0;
      this.currentMessageUpdateMs = msgUpdate;
      const olderMessage = msgUpdate > 0 && this.lastIncidentUpdateMs > 0 && msgUpdate <= this.lastIncidentUpdateMs;
      if (olderMessage) {
        popups.length = 0;
        phaseEvent = null;
      }
      const validPopups = popups.filter(p => NEUTRAL_OK(p) || (p.ParticipantPosition && String(p.ParticipantPosition) !== "0"));
      if (validPopups.length) {
        // The feed's CurrentIncident is what the provider says is happening
        // now, so it wins over stat-derived candidates — except a goal read
        // from the score, when the feed hasn't labelled it as a goal yet.
        // Without a CurrentIncident, the most significant stat change wins.
        const feed = validPopups.find(p => p.source === 'feed');
        const scoreGoal = validPopups.find(p => p.source === 'score');
        let pick;
        const varReview = validPopups.find(p => p.source === 'var');
        if (scoreGoal && !(feed && GOAL_TYPES.includes(popupType(feed)))) pick = scoreGoal;
        // A VAR review starting beats the feed's incident, unless that's
        // already something as big (goal, red card, the VAR itself).
        else if (varReview && !(feed && popupPriority(feed) >= popupPriority(varReview))) pick = varReview;
        else if (feed) pick = feed;
        else {
          const priority = isAfl ? (inc => AFL_PRIORITY[popupType(inc)] ?? 20) : popupPriority;
          pick = validPopups.reduce((a, b) => (priority(b) > priority(a) ? b : a));
        }
        this.triggerPopupEvent(pick);
      } else if (phaseEvent) {
        this.triggerPopupEvent(phaseEvent);
      } else if ((isFootball || isHockey || isHandball || isAfl) && !this.isInitialLoad && !olderMessage) {
        // Nothing changed in this update (no stat, score, VAR or feed
        // incident): show which team has the ball, from the extra data's
        // "Possession" entry.
        const poss = extra.find(e => e && String(e.Name || e.name || '').toLowerCase() === 'possession');
        const val = poss ? String(poss.Value ?? poss.value).toLowerCase() : '';
        const side = val === 'home' || val === '1' ? '1' : (val === 'away' || val === '2' ? '2' : null);
        const cur = this.specialEvent;
        if (!side) {
          // no possession entry, or one without a side: nothing to announce
        } else if (cur && popupType(cur) === POSSESSION_TYPE && String(cur.ParticipantPosition) === side) {
          // already showing this team's possession: nothing to announce
        } else if (cur && popupType(cur) !== POSSESSION_TYPE && Date.now() - cur.uniqueKey < POSSESSION_HOLD_MS) {
          // another incident is still on screen: possession held back
        } else {
          const possInc = { IncidentType: POSSESSION_TYPE, ParticipantPosition: side, source: 'possession' };
          this.triggerPopupEvent(possInc);
        }
      }
      if (this.isInitialLoad) this.isInitialLoad = false;
    },
    // Hockey / handball stat candidates: `aliases` read as another type
    // (hockey video reviews as VAR), and the goal-kind stats (power play,
    // short-handed, empty net, penalty shot) are not incidents of their own —
    // they label the goal they come with, in this update or in the next one.
    resolveGoalKindPopups(popups, goalKinds, aliases = {}) {
      popups.forEach(p => {
        const t = popupType(p);
        if (aliases[t] && p.source !== 'feed') p.IncidentType = aliases[t];
      });
      const kinds = popups.filter(p => p.source !== 'feed' && goalKinds[popupType(p)]);
      if (!kinds.length) return;
      kinds.forEach(k => popups.splice(popups.indexOf(k), 1));
      const sameSide = g => g && kinds.find(k => String(k.ParticipantPosition) === String(g.ParticipantPosition));
      const goal = popups.find(p => GOAL_TYPES.includes(popupType(p)));
      const kind = sameSide(goal);
      if (goal && kind) {
        popups.forEach(p => { if (GOAL_TYPES.includes(popupType(p))) p.goalKind = popupType(kind); });
        return;
      }
      const cur = this.specialEvent;
      const late = sameSide(cur && GOAL_TYPES.includes(popupType(cur)) && Date.now() - cur.uniqueKey < this.goalHoldMs ? cur : null);
      if (late) {
        this.specialEvent = { ...cur, goalKind: popupType(late) };
      }
    },
    // Hockey: the penalised team's last penalty ran out on the rink clock.
    onPowerPlayEnd(side) {
      this.triggerPopupEvent({ IncidentType: POWERPLAY_END_TYPE, ParticipantPosition: side, source: 'powerplay' });
    },
    buildPhaseAction(type, key) {
      const text = this.$t(PHASE_I18N[type]);
      return {
        uniqueKey: key,
        category: 'center',
        type,
        side: '0',
        teamName: this.scoreLine,
        playerName: '',
        text,
        icon: '',
        color: '#94a3b8',
        shadowColor: 'rgba(0,0,0,0.4)',
        fullTooltip: `${text} - ${this.scoreLine}`,
        isGoal: false,
        isPossession: false
      };
    },
    toggleSize() {
      this.$root.$emit('toggle-tracker-size', !this.isExpanded);
    },
    changeTab(tab){
      this.mainViewTab = tab;
    },
    resolvePlayerName(inc, type = 'in') {
      if (!inc) return '';
      if (inc.Players || inc.players) {
        const playersObj = inc.Players || inc.players;
        if (type === 'in' && playersObj.Item1 && playersObj.Item1.length > 0) return playersObj.Item1[0].Name || playersObj.Item1[0].name || '';
        if (type === 'out' && playersObj.Item2 && playersObj.Item2.length > 0) return playersObj.Item2[0].Name || playersObj.Item2[0].name || '';
      }
      let name = type === 'in' ? (inc.PlayerName || inc.playerName) : (inc.PlayerOutName || inc.playerOutName);
      if (name) return name;
      const id = type === 'in' ? (inc.PlayerId || inc.playerId) : (inc.PlayerOutId || inc.playerOutId);
      if (id && String(id) !== "-1") {
        const pStats = this.ls && (this.ls.PlayerStatistics || this.ls.playerStatistics) ? (this.ls.PlayerStatistics || this.ls.playerStatistics) : [];
        const p = pStats.find(x => String(x.PlayerId || x.playerId) == String(id));
        if (p) return p.PlayerName || p.playerName || '';
      }
      return '';
    },
    // What the VAR is reviewing ("Penalty", "Goal", ...) in the user's language.
    varReasonText(raw) {
      if (!raw) return '';
      const key = VAR_REASON_I18N[String(raw).toLowerCase().replace(/[^a-z]/g, '')];
      return key ? this.$t(key) : String(raw);
    },
    triggerPopupEvent(inc) {
      if (this.isInitialLoad) return;
      if (!NEUTRAL_OK(inc) && (!inc.ParticipantPosition || inc.ParticipantPosition === "0")) return;
      const prev = this.specialEvent;
      const holdExempt = [...GOAL_TYPES, VAR_TYPE, ...(this.isHandball ? HANDBALL_HOLD_EXEMPT : [])];
      if (this.goalHoldMs && prev && GOAL_TYPES.includes(popupType(prev)) && !holdExempt.includes(popupType(inc)) && Date.now() - prev.uniqueKey < this.goalHoldMs) {
        return;
      }
      if (prev && Date.now() - prev.uniqueKey < 5000) {
        const t = popupType(inc);
        const prevT = popupType(prev);
        // Echoes of the incident just shown, arriving in the next update:
        // total shots after a shot/goal, shots on target after a goal.
        if (t === 2046 && [2, 3, 16, 18, ...GOAL_TYPES].includes(prevT)) return;
        if (t === 2 && GOAL_TYPES.includes(prevT)) return;
        // Same incident again (e.g. first from the stats, then from the feed
        // with the player's name): refresh its data, don't re-announce it.
        if (t === prevT && String(inc.ParticipantPosition) === String(prev.ParticipantPosition)) {
          this.specialEvent = { ...inc, uniqueKey: prev.uniqueKey };
          this.lastIncidentUpdateMs = Math.max(this.lastIncidentUpdateMs, this.currentMessageUpdateMs);
          return;
        }
      }
      // No expiry: the incident stays on screen until the next one replaces it.
      this.specialEvent = { ...inc, uniqueKey: Date.now() };
      this.lastIncidentUpdateMs = Math.max(this.lastIncidentUpdateMs, this.currentMessageUpdateMs);
    },
    getVolleySetPoints() {
      if (!this.ls) return { h: "0", a: "0" };
      const sb = this.ls.Scoreboard || this.ls.scoreboard;
      const currentPeriod = parseInt(sb?.CurrentPeriod || sb?.currentPeriod || 0);
      const periods = this.ls.Periods || this.ls.periods || [];
      const activeSet = periods.find(p => parseInt(p.Type || p.type) === currentPeriod);
      if (activeSet) {
        const res = activeSet.Results || activeSet.results || [];
        const h = res.find(r => String(r.Position || r.position) === "1");
        const a = res.find(r => String(r.Position || r.position) === "2");
        return {
          h: String(h?.Value ?? h?.value ?? "0"),
          a: String(a?.Value ?? a?.value ?? "0")
        };
      }
      return { h: "0", a: "0" };
    },
    showTooltip(e, text) {
      this.tooltip.text = text;
      this.tooltip.visible = true;
      this.tooltip.x = e.clientX + 15;
      this.tooltip.y = e.clientY + 15;
    },
    updateTooltipPos(e) {
      if (this.tooltip.visible) {
        this.tooltip.x = e.clientX + 15;
        this.tooltip.y = e.clientY + 15;
      }
    },
    hideTooltip() {
      this.tooltip.visible = false;
    },
    // Aussie rules: quarter `type` is over (the feed marks it finished).
    aflQuarterDone(type) {
      const periods = (this.ls && (this.ls.Periods || this.ls.periods)) || [];
      const q = periods.find(x => parseInt(x.Type ?? x.type) === type);
      return !!q && !!(q.IsFinished ?? q.isFinished);
    },
    statPair(type) {
      return { h: parseInt(this.getStat(type, '1')) || 0, a: parseInt(this.getStat(type, '2')) || 0 };
    },
    getStat(type, pos) {
      const ls = this.matchData && (this.matchData.Livescore || this.matchData.livescore);
      if (!ls) return 0;
      const stats = ls.Statistics || ls.statistics || [];
      const s = stats.find(stat => String(stat.Type || stat.type) == String(type));
      if (!s) return 0;
      const res = s.Results || s.results || [];
      const record = res.find(r => String(r.Position || r.position) == String(pos));
      return record ? (record.Value ?? record.value ?? 0) : 0;
    },
    startClock() {
      if (this.timerId) return;
      this.timerId = setInterval(() => {
        if (this.isCountdownSport) {
          if (this.localSeconds > 0) this.localSeconds--;
        } else {
          this.localSeconds++;
        }
      }, 1000);
    },
    stopClock() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }
  },
  computed: {
    ...mapGetters({
      getSkinLogoUrl: 'settings/getSkinLogoUrl',
      getShowSirplayLicence: 'settings/getShowSirplayLicence',
    }),
    // Ad-boards: the skin's logo; the Sirplay one only when the skin shows the
    // Sirplay licence, otherwise the boards show their text.
    boardLogoUrl() {
      return resolveBoardLogo(this.getSkinLogoUrl, this.getShowSirplayLicence, BOARD_LOGO);
    },
    homeName() {
      if (this.matchData?.home) return this.matchData.home;
      const p = this.matchData?.participants?.find(x => String(x.position) === '1' || String(x.Position) === '1');
      return p?.name || p?.Name || this.$t('statistics.hometeam');
    },
    awayName() {
      if (this.matchData?.away) return this.matchData.away;
      const p = this.matchData?.participants?.find(x => String(x.position) === '2' || String(x.Position) === '2');
      return p?.name || p?.Name || this.$t('statistics.awayteam');
    },
    ls() { return this.matchData?.Livescore || this.matchData?.livescore || this.matchData || null; },
    isTennis() { return parseInt(this.matchData && this.matchData.Fixture && this.matchData.Fixture.Sport ? this.matchData.Fixture.Sport.Id : (this.matchData && this.matchData.sportId ? this.matchData.sportId : 0)) === 54094 },
    isTableTennis() {
      return String(this.matchData?.Fixture?.Sport?.Id || this.matchData?.sportId || this.initialSportId || '') === '265917';
    },
    isVolley() {
      const id = parseInt(this.matchData?.sportId || this.initialSportId || 0);
      return [154830, 621569, 1149113].includes(id);
    },
    // Indoor volleyball: VolleyArena (beach volley keeps the generic pitch).
    isVolleyball() {
      return String(this.matchData?.sportId || this.initialSportId || '') === VOLLEYBALL_SPORT_ID;
    },
    volleyPoints() {
      return this.getVolleySetPoints();
    },
    // Aces so far (stat 20), for tennis and volleyball.
    aceCounts() {
      return this.statPair(20);
    },
    // Volleyball stat totals: aces, service errors (2010 / 2068, the feed
    // may fill either), time-outs (31).
    volleyStats() {
      const e1 = this.statPair(2010);
      const e2 = this.statPair(2068);
      return {
        aces: this.aceCounts,
        serviceErrors: { h: Math.max(e1.h, e2.h), a: Math.max(e1.a, e2.a) },
        timeouts: this.statPair(31)
      };
    },
    isCountdownSport() {
      const sId = parseInt(this.matchData?.sportId || 0);
      const basketIds = [48242, 1149105, 1149097];
      const hockeyIds = [35232, 530129, 46957];
      // Aussie rules: the clock shows the time left in the quarter.
      return [...basketIds, ...hockeyIds, parseInt(AFL_SPORT_ID)].includes(sId);
    },
    displayTime() {
      const totalSeconds = Math.max(0, this.localSeconds);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    homeScore() {
      const res = this.ls && (this.ls.Scoreboard && this.ls.Scoreboard.Results ? this.ls.Scoreboard.Results : (this.ls.scoreboard && this.ls.scoreboard.results ? this.ls.scoreboard.results : []));
      const r = res.find(x => String(x.Position || x.position) == "1");
      return r ? (r.Value ?? r.value ?? 0) : 0;
    },
    awayScore() {
      const res = this.ls && (this.ls.Scoreboard && this.ls.Scoreboard.Results ? this.ls.Scoreboard.Results : (this.ls.scoreboard && this.ls.scoreboard.results ? this.ls.scoreboard.results : []));
      const r = res.find(x => String(x.Position || x.position) == "2");
      return r ? (r.Value ?? r.value ?? 0) : 0;
    },
    currentMatchProgressX() {
      const matchDurationSeconds = this.sportDurationConfig.duration;
      let percentage = (this.localSeconds / matchDurationSeconds) * 1000;
      if (percentage > 1000) percentage = 1000;
      return isNaN(percentage) ? 0 : percentage;
    },
    allIncidents() {
      if (!this.ls) return [];
      let list = [...this.liveIncidentsHistory];
      const rootInc = this.ls.CurrentIncident || this.ls.currentIncident;
      if (rootInc && parseInt(rootInc.IncidentType || rootInc.incidentType || 0) !== 0) {
        list.push(rootInc);
      }
      const periods = this.ls.Periods || this.ls.periods || [];
      periods.forEach(p => { (p.Incidents || p.incidents || []).forEach(i => list.push(i)); });
      const stats = this.ls.Statistics || this.ls.statistics || [];
      stats.forEach(s => { (s.Incidents || s.incidents || []).forEach(i => list.push(i)); });
      const uniqueIncs = [];
      const seen = new Set();
      list.forEach(i => {
        const t = parseInt(i.IncidentType || i.incidentType || 0);
        let s = parseInt(i.Seconds || i.seconds);
        if (isNaN(s)) {
          let m = parseInt(i.Time || i.time);
          if (!isNaN(m)) s = m * 60; else s = 0;
        }
        const side = String(i.ParticipantPosition || i.participantPosition || '');
        const id = i.Id || i.id || i.uniqueId || `${t}-${s}-${side}-${i.PlayerId || i.playerId || '0'}`;
        if (!seen.has(id)) {
          seen.add(id);
          i.calculatedSeconds = s;
          uniqueIncs.push(i);
        }
      });
      return uniqueIncs.sort((a, b) => b.calculatedSeconds - a.calculatedSeconds);
    },
    glowClass() {
      if (this.matchPhase) return '';
      // An attack / dangerous attack on screen decides the side: the feed's
      // possession field can lag behind (still showing the other team), which
      // would light the wedge in the wrong half while the incident strip says
      // the opposite.
      const a = this.displayAction;
      if (a && [4, 5, 11, 2438].includes(a.type) && (a.side === '1' || a.side === '2')) {
        return a.side === '1' ? 'glow-home' : 'glow-away';
      }
      // Football with nothing on the pitch yet: no wedge from possession alone.
      if (!a && ['soccer', 'futsal'].includes(this.sportConfig.lines)) return '';
      if (this.ls) {
        const extra = this.ls.LivescoreExtraData || this.ls.livescoreExtraData || [];
        const poss = extra.find(d => String(d.Name || d.name).toLowerCase() === "possession");
        if (poss) {
          const val = String(poss.Value || poss.value).toLowerCase();
          if (val === 'home' || val === '1') return 'glow-home';
          if (val === 'away' || val === '2') return 'glow-away';
        }
      }
      return '';
    },

    currentAction() {
      if (!this.ls) return null;
      // During the break / after the final whistle the phase itself is the
      // incident on screen, whatever was shown before.
      if (this.matchPhase === 'halftime' || this.matchPhase === 'fulltime') {
        const phaseType = this.matchPhase === 'halftime' ? PHASE.HALFTIME : PHASE.FULLTIME;
        return this.buildPhaseAction(phaseType, `phase-${phaseType}`);
      }
      if (this.specialEvent && PHASE_TYPES.includes(popupType(this.specialEvent))) {
        return this.buildPhaseAction(popupType(this.specialEvent), `phase-${this.specialEvent.uniqueKey}`);
      }
      let type = 0;
      let side = null;
      let inc = null;
      let category = 'directional';
      let fromPossession = false;
      if (this.specialEvent) {
        inc = this.specialEvent;
        type = parseInt(inc.IncidentType || inc.incidentType || inc.id || 0);
        side = String(inc.ParticipantPosition || inc.participantPosition || '0');
        category = TRACKER_STYLES[type] ? TRACKER_STYLES[type].category : 'center';
      } else {
        // Football: nothing has happened since the tracker opened, so the
        // pitch stays empty — no incident dug out of the feed's history (it
        // could be minutes old, e.g. an early corner) and no attack guessed
        // from possession. The first live incident is the first one shown.
        const isFootball = ['soccer', 'futsal'].includes(this.sportConfig.lines);
        if (isFootball) return null;
        // Ice hockey / handball: before the first live incident, just who
        // has the puck / the ball.
        if (this.isHockey || this.isHandball || this.isAussieRules) {
          if (!this.possessionSide) return null;
          side = this.possessionSide;
          type = POSSESSION_TYPE;
          fromPossession = true;
        }
        // Other sports: show the latest incident the feed already has.
        const eventsToShowOnPitch = [1, 2, 3, 4, 5, 6, 7, 9, 10, 12, 13, 14, 15, 16, 17, 18, 20, 21, 23, 24, 25, 27, 96, 1074, 2158];
        const validAction = !this.isHockey && !this.isHandball && !this.isAussieRules && this.allIncidents.find(i => {
          const t = parseInt(i.IncidentType || i.incidentType || i.id || 0);
          if (isFootball) { const r = resolveFootballType(t); return !r.ignored && t !== 0; }
          return eventsToShowOnPitch.includes(t);
        });
        if (validAction) {
          inc = validAction;
          type = parseInt(validAction.IncidentType || validAction.incidentType || validAction.id);
          if (isFootball) type = resolveFootballType(type).type;
          side = String(validAction.ParticipantPosition || validAction.participantPosition || '0');
          category = TRACKER_STYLES[type] ? TRACKER_STYLES[type].category : 'center';
        } else if (!this.isHockey && !this.isHandball && !this.isAussieRules) {
          const extra = this.ls.LivescoreExtraData || this.ls.livescoreExtraData || [];
          const poss = extra.find(d => String(d.Name || d.name).toLowerCase().includes("possession"));
          if (poss) {
            const val = String(poss.Value || poss.value).toLowerCase();
            if (val === 'home' || val === '1') { side = '1'; type = 4; fromPossession = true; }
            else if (val === 'away' || val === '2') { side = '2'; type = 4; fromPossession = true; }
          }
        }
      }
      if (type === 0 || !side || (side === '0' && type !== VAR_TYPE)) return null;
      let teamName = "";
      if (side === '1') teamName = this.homeName;
      else if (side === '2') teamName = this.awayName;
      // VAR: team (when the feed names one) and what is under review.
      if (type === VAR_TYPE) teamName = [teamName, this.varReasonText(inc && inc.varText)].filter(Boolean).join(' - ');
      const playerName = this.resolvePlayerName(inc, 'in');
      const sportId = String(this.matchData?.sportId || this.matchData?.Fixture?.Sport?.Id || "");
      const styleDef = TRACKER_STYLES[type] || { category: 'center', icon: '⚡', color: '#00e5ff', shadow: 'rgba(0,229,255,0.4)' };
      let actionText = "";
      if (type === 4) actionText = this.$t('statistics.attacks');
      else if (type === 5) actionText = this.$t('statistics.dangerousattacks');
      else if (this.statNames[type]) actionText = this.$t(this.statNames[type]);
      else actionText = this.$t('statistics.action');
      const goalSports = ["6046", "274792", "687887", "1149117", "621561", "35234", "1149111", "1149121"];
      if (goalSports.includes(sportId)) {
        if ([9, 25].includes(type)) {
          actionText = this.$t('statistics.goal');
        } else if (type === 96) {
          actionText = this.$t('statistics.save');
        }
      } else {
        const pointSports = ["48242", "1149105", "54094", "154830", "621569", "1149113"];
        if (pointSports.includes(sportId)) {
          if ([9, 25, 27].includes(type)) actionText = "PUNTO!";
          if (type === 20) actionText = "ACE!";
          if (type === 21) actionText = this.$t('statistics.doublefaults');
        }
      }
      if ((sportId === "48242" || sportId === "1149105") && [9, 25, 27].includes(type)) actionText = this.$t('statistics.point');
      if (type === 30) actionText = this.$t('statistics.threepoints');
      if (type === 28) actionText = this.$t('statistics.twopoints');
      if (type === 32) actionText = this.$t('statistics.freethrows');
      if (['soccer', 'futsal'].includes(this.sportConfig.lines) && footballIncidentKey(type)) {
        actionText = this.$t(footballIncidentKey(type));
      }
      if (this.isHockey && HOCKEY_I18N[type]) actionText = this.$t(HOCKEY_I18N[type]);
      if (this.isHockey && inc && HOCKEY_GOAL_KINDS[inc.goalKind]) actionText = this.$t(HOCKEY_GOAL_KINDS[inc.goalKind]);
      if (this.isHandball && handballIncidentKey(type)) actionText = this.$t(handballIncidentKey(type));
      if (this.isHandball && inc && HANDBALL_GOAL_KINDS[inc.goalKind]) actionText = this.$t(HANDBALL_GOAL_KINDS[inc.goalKind]);
      if (this.isAussieRules && aflIncidentKey(type)) actionText = this.$t(aflIncidentKey(type));
      actionText = actionText.replace(/[➤➤►]/g, '').trim();
      // Announced incidents carry their own arrival stamp: two identical
      // incidents in a row (e.g. two shots off target) still read as new.
      const seconds = inc ? (inc.uniqueKey || inc.calculatedSeconds || 0) : 'possession';
      const minute = Math.floor((inc ? (inc.calculatedSeconds || 0) : this.localSeconds) / 60);
      const isGoalLike = [9, 24, 25, 27, 28, 30, 32, 20].includes(type);
      return {
        uniqueKey: `${category}-${type}-${side}-${seconds}`,
        category: isGoalLike ? 'center' : styleDef.category,
        type,
        side,
        teamName,
        playerName,
        text: actionText,
        icon: styleDef.icon,
        color: styleDef.color,
        shadowColor: styleDef.shadow,
        fullTooltip: `${minute}' - ${teamName} - ${actionText}`,
        isGoal: isGoalLike,
        isPossession: fromPossession
      };
    },
    isBasket() {
      return this.sportConfig.lines === 'basket';
    },
    isDarts() {
      return String(this.matchData?.sportId || this.initialSportId || '') === '154923';
    },
    isHockey() {
      return String(this.matchData?.sportId || this.initialSportId || '') === HOCKEY_SPORT_ID;
    },
    isBaseball() {
      return String(this.matchData?.sportId || this.initialSportId || '') === BASEBALL_SPORT_ID;
    },
    isHandball() {
      return String(this.matchData?.sportId || this.initialSportId || '') === HANDBALL_SPORT_ID;
    },
    isAussieRules() {
      return String(this.matchData?.sportId || this.initialSportId || '') === AFL_SPORT_ID;
    },
    // Aussie rules scoreboard period: 1-4 quarters, 40 extra time.
    aflPeriod() {
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      return parseInt(sb?.CurrentPeriod ?? sb?.currentPeriod ?? 0) || 0;
    },
    // Between quarters the feed has no break period: CurrentPeriod already
    // names the next quarter, its clock is stopped (status 0) and reset to a
    // full quarter (15 / 17 / 20 minutes), that quarter has no score and no
    // incidents yet, and the one before it is finished. A clock stopped
    // during play has less than a full quarter left.
    aflBreak() {
      if (!this.isAussieRules || this.scoreboardFinished) return false;
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      if (!sb || parseInt(sb.Status ?? sb.status) !== 2) return false;
      const p = this.aflPeriod;
      if (p <= 1) return false;
      const clock = sb.Clock || sb.clock;
      if (parseInt(clock?.Status ?? clock?.status ?? 0) === 1) return false;
      if (![900, 1020, 1200].includes(parseInt(clock?.Seconds ?? clock?.seconds))) return false;
      const periods = (this.ls.Periods || this.ls.periods) || [];
      const cur = periods.find(x => parseInt(x.Type ?? x.type) === p);
      const started = cur && ((cur.Incidents || cur.incidents || []).length > 0 ||
        (cur.Results || cur.results || []).some(x => parseInt(x.Value ?? x.value) > 0) || (cur.IsFinished ?? cur.isFinished));
      return !started && this.aflQuarterDone(p === 40 ? 4 : p - 1);
    },
    aflPeriodText() {
      const p = this.aflPeriod;
      if (this.scoreboardFinished) return this.$t('incidents.ft');
      if (this.aflBreak) return this.$t(p === 3 ? 'afl.halftime' : 'afl.quartertime');
      if (p >= 1 && p <= 4) return this.$t('afl.quarter', { n: p });
      if (p === 40) return this.$t('afl.ot');
      return '';
    },
    // Quarters / extra time in order, for the final badge.
    aflPeriods() {
      const periods = (this.ls && (this.ls.Periods || this.ls.periods)) || [];
      return periods
        .filter(p => { const t = parseInt(p.Type ?? p.type); return (t >= 1 && t <= 4) || t === 40; })
        .sort((a, b) => parseInt(a.Type ?? a.type) - parseInt(b.Type ?? b.type))
        .map(p => {
          const res = p.Results || p.results || [];
          const val = pos => { const r = res.find(x => String(x.Position || x.position) === pos); return r ? (r.Value ?? r.value ?? 0) : 0; };
          return { h: val('1'), a: val('2') };
        });
    },
    // Goals and behinds kicked so far (the arena shows them as goals.behinds).
    aflGoals() {
      return this.statPair(AFL_GOALS_STAT);
    },
    aflBehinds() {
      return this.statPair(AFL_BEHIND_TYPE);
    },
    // How long a goal on screen holds back the smaller incidents after it.
    goalHoldMs() {
      if (this.isHockey) return HOCKEY_GOAL_HOLD_MS;
      if (this.isHandball) return HANDBALL_GOAL_HOLD_MS;
      if (this.isAussieRules) return AFL_GOAL_HOLD_MS;
      return 0;
    },
    // Handball half time: from the scoreboard, or BreakTime named by the feed.
    handballBreak() {
      if (!this.isHandball || this.scoreboardFinished) return false;
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      const p = parseInt(sb?.CurrentPeriod ?? sb?.currentPeriod ?? 0);
      return p === 31 || p === 80 || this.feedBreak;
    },
    // Half on the court clock: 1st / 2nd half, extra time, 7-metre shootout, break.
    handballPeriodText() {
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      const p = parseInt(sb?.CurrentPeriod ?? sb?.currentPeriod ?? 0);
      if (this.scoreboardFinished) return this.$t('incidents.ft');
      if (this.handballBreak) return this.$t('handball.break');
      if (p === 10) return this.$t('handball.firsthalf');
      if (p === 20) return this.$t('handball.secondhalf');
      if ([30, 35, 40].includes(p)) return this.$t('handball.ot');
      if (p === 50) return this.$t('handball.shootout');
      return '';
    },
    // Goals per half (extra time and shootout included) for the final badge.
    handballPeriods() {
      const periods = (this.ls && (this.ls.Periods || this.ls.periods)) || [];
      return periods
        .filter(p => [10, 20, 30, 35, 40, 50].includes(parseInt(p.Type ?? p.type)))
        .sort((a, b) => parseInt(a.Type ?? a.type) - parseInt(b.Type ?? b.type))
        .map(p => {
          const res = p.Results || p.results || [];
          const val = pos => { const r = res.find(x => String(x.Position || x.position) === pos); return r ? (r.Value ?? r.value ?? 0) : 0; };
          return { h: val('1'), a: val('2') };
        });
    },
    // Period on the rink clock: P1-P3, overtime, shootout, break.
    hockeyPeriodText() {
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      const p = parseInt(sb?.CurrentPeriod ?? sb?.currentPeriod ?? 0);
      if (this.scoreboardFinished) return this.$t('incidents.ft');
      if (p >= 80 || p === 31 || this.feedBreak) return this.$t('hockey.break');
      if (p >= 1 && p <= 3) return 'P' + p;
      if (p === 40) return this.$t('hockey.ot');
      if (p === 50) return this.$t('hockey.so');
      return '';
    },
    // Goals per period (overtime and shootout included) for the final badge.
    hockeyPeriods() {
      const periods = (this.ls && (this.ls.Periods || this.ls.periods)) || [];
      return periods
        .filter(p => { const t = parseInt(p.Type ?? p.type); return (t >= 1 && t <= 3) || t === 40 || t === 50; })
        .sort((a, b) => parseInt(a.Type ?? a.type) - parseInt(b.Type ?? b.type))
        .map(p => {
          const res = p.Results || p.results || [];
          const val = pos => { const r = res.find(x => String(x.Position || x.position) === pos); return r ? (r.Value ?? r.value ?? 0) : 0; };
          return { h: val('1'), a: val('2') };
        });
    },
    // Penalty on screen: the card on the rink and its penalty clock.
    hockeyPenalty() {
      const a = this.displayAction;
      if (!this.isHockey || !a || !HOCKEY_PENALTY_MINUTES[a.type] || (a.side !== '1' && a.side !== '2')) return null;
      return { key: a.uniqueKey, side: a.side, minutes: HOCKEY_PENALTY_MINUTES[a.type], team: a.teamName };
    },
    // Darts livescore: period 67 holds the points left in the current leg,
    // the set periods (1, 2, ...) the legs won in each set, the scoreboard
    // the sets won.
    dartsData() {
      const ls = this.ls || {};
      const periods = ls.Periods || ls.periods || [];
      const sb = ls.Scoreboard || ls.scoreboard || {};
      const typeOf = p => parseInt(p.Type ?? p.type);
      const pair = res => {
        const val = pos => {
          const r = (res || []).find(x => String(x.Position ?? x.position) === pos);
          const n = parseInt(r?.Value ?? r?.value);
          return isNaN(n) ? null : n;
        };
        return { h: val('1'), a: val('2') };
      };
      const results = p => p && (p.Results || p.results);
      const setPeriods = periods.filter(p => typeOf(p) >= 1 && typeOf(p) < 60);
      const current = parseInt(sb.CurrentPeriod ?? sb.currentPeriod);
      const legPeriod = setPeriods.find(p => typeOf(p) === current) || setPeriods[setPeriods.length - 1];
      const legs = pair(results(legPeriod));
      const sets = pair(sb.Results || sb.results);
      return {
        remaining: pair(results(periods.find(p => typeOf(p) === 67))),
        legs: { h: legs.h || 0, a: legs.a || 0 },
        sets: { h: sets.h || 0, a: sets.a || 0 },
        setNo: setPeriods.length ? Math.max(...setPeriods.map(typeOf)) : 1,
        // A legs-only match has a single set period (its scoreboard still
        // turns 0-1 at the end).
        hasSets: setPeriods.length > 1
      };
    },
    // Points per quarter (overtimes included) for the basketball final badge.
    basketPeriods() {
      const periods = (this.ls && (this.ls.Periods || this.ls.periods)) || [];
      return periods
        .filter(p => { const t = parseInt(p.Type ?? p.type); return (t >= 1 && t <= 4) || t === 40; })
        .map(p => {
          const res = p.Results || p.results || [];
          const val = pos => { const r = res.find(x => String(x.Position || x.position) === pos); return r ? (r.Value ?? r.value ?? 0) : 0; };
          return { h: val('1'), a: val('2') };
        });
    },
    // Not kicked off yet: scoreboard / event status 1 (not started) or 9 (about to start).
    isPreMatch() {
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      const status = String(sb ? (sb.Status ?? sb.status ?? '') : (this.matchData?.status ?? ''));
      return status === '1' || status === '9';
    },
    // Match over, from the scoreboard status / final period (tennis, basketball).
    scoreboardFinished() {
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      if (!sb) return false;
      const status = parseInt(sb.Status ?? sb.status ?? 0);
      const period = parseInt(sb.CurrentPeriod ?? sb.currentPeriod ?? 0);
      return status === 3 || period >= 100;
    },
    // Football match phase from the scoreboard: 'halftime' | 'fulltime' | null.
    matchPhase() {
      if (!['soccer', 'futsal'].includes(this.sportConfig.lines) || !this.ls) return null;
      const sb = this.ls.Scoreboard || this.ls.scoreboard;
      if (!sb) return null;
      const period = parseInt(sb.CurrentPeriod ?? sb.currentPeriod ?? 0);
      const status = parseInt(sb.Status ?? sb.status ?? 0);
      if (status === 3 || period >= 100) return 'fulltime';
      if (period === 31 || period === 80 || this.feedBreak) return 'halftime';
      // Some feeds keep period 10 during the break, with the clock stopped
      // past 45'.
      const clock = sb.Clock || sb.clock;
      const clockStopped = parseInt(clock?.Status ?? clock?.status ?? 1) === 2;
      if (period === 10 && clockStopped && this.localSeconds >= 45 * 60) return 'halftime';
      return null;
    },
    // Team in possession according to the feed's extra data ('1' | '2' | null).
    possessionSide() {
      const extra = (this.ls && (this.ls.LivescoreExtraData || this.ls.livescoreExtraData)) || [];
      const poss = extra.find(d => String(d.Name || d.name).toLowerCase() === 'possession');
      if (!poss) return null;
      const val = String(poss.Value ?? poss.value).toLowerCase();
      if (val === 'home' || val === '1') return '1';
      if (val === 'away' || val === '2') return '2';
      return null;
    },
    // Team colours from event-detail's competitors[].shirtColorPrimary,
    // falling back to the default celeste/red. If both shirts are nearly the
    // same colour, the away team uses its secondary shirt colour (or the
    // default away colour) so the two sides stay distinguishable.
    teamColors() {
      return resolveTeamColors(this.matchData);
    },
    teamColorVars() {
      const { home, away } = this.teamColors;
      return {
        '--color-home': home,
        '--color-away': away,
        '--color-home-contrast': contrastText(home),
        '--color-away-contrast': contrastText(away)
      };
    },
    scoreLine() {
      return `${this.homeName} ${this.homeScore} - ${this.awayScore} ${this.awayName}`;
    },
    // Clock label on the pitch: the running time, or HT/FT during the phases.
    stadiumClock() {
      if (this.matchPhase === 'halftime') return this.$t('incidents.ht');
      if (this.matchPhase === 'fulltime') return this.$t('incidents.ft');
      return this.displayTime;
    },
    displayAction() {
      return this.currentAction;
    },
    // Short label shown as a badge on the pitch for shots, offsides & co.
    pitchBadgeText() {
      const a = this.displayAction;
      if (!a || PHASE_TYPES.includes(a.type)) return '';
      const unknownFootball = ['soccer', 'futsal'].includes(this.sportConfig.lines) && !FOOTBALL_INCIDENTS[a.type] && !BIG_BADGE_KINDS[a.type];
      return PITCH_BADGE_TYPES.includes(a.type) || unknownFootball ? a.text : '';
    },
    // Full-screen goal panel: the score before the goal (so the scoring
    // team's number can roll up to the current one) and the live score.
    goalCelebration() {
      const a = this.displayAction;
      if (!a || !GOAL_TYPES.includes(a.type) || !(['soccer', 'futsal'].includes(this.sportConfig.lines) || this.isHockey || this.isHandball || this.isAussieRules)) return null;
      const home = parseInt(this.homeScore) || 0;
      const away = parseInt(this.awayScore) || 0;
      const rc = this.recentScoreChange && Date.now() - this.recentScoreChange.at < 20000 ? this.recentScoreChange : null;
      // Own goal: the other team's score goes up (unless the scoreboard
      // already told us which side did).
      const scoreSide = rc ? rc.side : (a.type === 24 ? (a.side === '1' ? '2' : '1') : a.side);
      return {
        key: a.uniqueKey,
        title: this.isHockey ? this.$t('hockey.goal') : this.isHandball ? this.$t('handball.goal') : this.isAussieRules ? this.$t('afl.goal') : this.$t('incidents.goal') + '!',
        label: this.$t('incidents.goal'),
        // Hockey: a penalty-shot goal is scored from straight in front.
        penaltyShot: a.type === 25 || !!(this.specialEvent && this.specialEvent.goalKind === 25),
        subtitle: this.isHockey || this.isHandball ? (a.type !== 9 || (this.specialEvent && this.specialEvent.goalKind) ? a.text : '') : (a.type !== 9 ? a.text : ''),
        team: a.teamName,
        side: a.side,
        scoreSide,
        homeName: this.homeName,
        awayName: this.awayName,
        // Without a scoreboard change yet, start from the live score: the
        // number then rolls as soon as the scoreboard catches up.
        fromHome: rc ? rc.fromHome : home,
        fromAway: rc ? rc.fromAway : away,
        homeScore: home,
        awayScore: away
      };
    },
    bigBadge() {
      const a = this.displayAction;
      const kind = a && BIG_BADGE_KINDS[a.type];
      // Goals get the full-screen celebration instead (goalCelebration).
      if (!kind || (kind === 'goal' && this.goalCelebration)) return null;
      return {
        key: a.uniqueKey,
        kind,
        title: a.text + (kind === 'goal' ? '!' : ''),
        team: a.teamName,
        side: a.side,
        // Half time / full time stay up for as long as the phase lasts, a
        // VAR review for as long as the feed flags it in progress.
        persistent: [PHASE.HALFTIME, PHASE.FULLTIME].includes(a.type) || (kind === 'var' && this.lastVarValue !== null)
      };
    },
    // Football half time: summary cards (shots, corners, best players...)
    // the stadium rotates with the "half time" badge.
    breakSlides() {
      if (this.matchPhase !== 'halftime' || !this.isSoccerStadium) return [];
      return buildBreakSummary({
        ls: this.ls,
        matchData: this.matchData,
        playerStats: this.playerStats,
        homeName: this.homeName,
        awayName: this.awayName,
        t: key => this.$t(key)
      });
    },
    // The incident title and bar are coloured by which team the incident
    // belongs to, not by incident type — a card, a shot and a goal from
    // the same side all read in that side's colour.
    incidentSideColor() {
      if (!this.displayAction) return '';
      if (this.displayAction.side === '0') return '#94a3b8';
      return this.displayAction.side === '1' ? 'var(--color-home)' : 'var(--color-away)';
    },
    ballPosition() {
      if (!this.displayAction) {
        if (this.isTennis && this.servingSide) {
          return {
            x: this.servingSide === '1' ? 20 : 80,
            y: 32,
            opacity: 0.6
          };
        }
        return { x: 50, y: 50, opacity: 0 };
      }
      const type = this.displayAction.type;
      const side = this.displayAction.side;
      // Kick-offs put the ball on the centre spot; during the break / after
      // the match there's no ball on the pitch.
      if (type === PHASE.SECOND_HALF || type === PHASE.KICKOFF) return { x: 50, y: 50, opacity: 1 };
      if (PHASE_TYPES.includes(type)) return { x: 50, y: 50, opacity: 0 };
      let x = 50;
      let y = 50;
      let opacity = 1;
      if (this.isTennis && [27, 20, 23].includes(type)) {
        x = side === '1' ? 85 : 15;
        y = 32;
        return { x, y, opacity };
      }
      if (type === 1) {
        x = side === '1' ? 98 : 2;
        y = 5;
      } else if (type === 2 || type === 9 || type === 24 || type === 25 || type === 27 || type === 28 || type === 30 || type === 32) {
        x = side === '1' ? 95 : 5;
        y = 50;
      } else if (type === 16 || type === 2046) {
        x = side === '1' ? 88 : 12;
        y = 45;
      } else if (type === 3 || type === 18) {
        x = side === '1' ? 98 : 2;
        y = 25;
      } else if (type === 4) {
        x = side === '1' ? 65 : 35;
        y = 50;
      } else if (type === 5) {
        x = side === '1' ? 85 : 15;
        y = 50;
      } else if (type === 12 || type === 13) {
        x = side === '1' ? 75 : 25;
        y = 30;
      } else if (type === 15) {
        x = side === '1' ? 85 : 15;
        y = 20;
      } else if (type === 17) {
        x = side === '1' ? 60 : 40;
        y = 5;
      } else if (type === 14 || type === 88 || type === 96) {
        x = side === '1' ? 10 : 90;
        y = 50;
      } else if (type === 2389) {
        // Penalty saved: the side is the goalkeeper's team, at its own goal.
        x = side === '1' ? 5 : 95;
        y = 50;
      } else if (type === 2148) {
        x = side === '1' ? 92 : 8;
        y = 10;
      } else if (type === 2394) {
        x = side === '1' ? 78 : 22;
        y = 40;
      } else if (type === 2440) {
        x = side === '1' ? 94 : 6;
        y = 12;
      } else if (type === 11) {
        // Possession: the ball moves around the team's own half.
        x = side === '1' ? 36 : 64;
        y = 50;
      } else if (type === 2438 || type === 2430) {
        x = side === '1' ? 76 : 24;
        y = 50;
      } else if (type === 2115 || type === 2228) {
        x = side === '1' ? 40 : 60;
        y = 50;
      } else if (type === 2439) {
        x = side === '1' ? 58 : 42;
        y = 45;
      } else {
        x = 50;
        y = 50;
      }
      if (this.isVolley && this.displayAction) {
        const type = this.displayAction.type;
        const side = this.displayAction.side;
        if (type === 20) {
          return {
            x: side === '1' ? 92 : 8,
            y: 12,
            opacity: 1
          };
        } else if (type === 27 || this.displayAction.isGoal) {
          return {
            x: side === '1' ? 75 : 25,
            y: 32,
            opacity: 1
          };
        }
      }
      return { x, y, opacity };
    },
    sportDurationConfig() {
      const sId = String(this.matchData?.Fixture?.Sport?.Id || this.matchData?.sportId || "");
      if (["48242", "1149105", "1149097"].includes(sId)) {
        return { duration: 40 * 60, labels: [{ x: 250, text: "Q1", align: "middle" }, { x: 500, text: "HT", align: "middle" }, { x: 750, text: "Q3", align: "middle" }, { x: 980, text: "Q4", align: "end" }] };
      }
      if (["35232", "530129", "46957"].includes(sId)) {
        return { duration: 60 * 60, labels: [{ x: 333, text: "P1", align: "middle" }, { x: 666, text: "P2", align: "middle" }, { x: 980, text: "60'", align: "end" }] };
      }
      if ([HANDBALL_SPORT_ID].includes(sId)) {
        return { duration: 60 * 60, labels: [{ x: 250, text: "15'", align: "middle" }, { x: 500, text: "HT", align: "middle" }, { x: 750, text: "45'", align: "middle" }, { x: 980, text: "60'", align: "end" }] };
      }
      if ([AFL_SPORT_ID].includes(sId)) {
        return { duration: 80 * 60, labels: [{ x: 250, text: "Q1", align: "middle" }, { x: 500, text: "HT", align: "middle" }, { x: 750, text: "Q3", align: "middle" }, { x: 980, text: "Q4", align: "end" }] };
      }
      if (["131506"].includes(sId)) {
        return { duration: 60 * 60, labels: [{ x: 250, text: "Q1", align: "middle" }, { x: 500, text: "HT", align: "middle" }, { x: 750, text: "Q3", align: "middle" }, { x: 980, text: "60'", align: "end" }] };
      }
      if (["274792", "274791"].includes(sId)) {
        return { duration: 80 * 60, labels: [{ x: 250, text: "20'", align: "middle" }, { x: 500, text: "HT", align: "middle" }, { x: 750, text: "60'", align: "middle" }, { x: 980, text: "80'", align: "end" }] };
      }
      return { duration: 90 * 60, labels: [{ x: 333, text: "30'", align: "middle" }, { x: 500, text: "HT", align: "middle" }, { x: 666, text: "60'", align: "middle" }, { x: 833, text: "75'", align: "middle" }, { x: 980, text: "90'", align: "end" }] };
    },
    servingSide() {
      if (!this.ls) return null;
      const extra = this.ls.LivescoreExtraData || this.ls.livescoreExtraData || [];
      const turnObj = extra.find(x => String(x.Name || x.name).toLowerCase() === "turn");
      return turnObj ? String(turnObj.Value || turnObj.value) : null;
    },
    tiebreakScore() {
      const extra = this.ls?.LivescoreExtraData || this.ls?.livescoreExtraData;
      return extra?.find(d => (d.Name || d.name) === "Tiebreak Score")?.Value || null;
    },
    currentGamePoints() {
      if (!this.ls) return { h: "0", a: "0" };
      const sb = this.ls.Scoreboard || this.ls.scoreboard;
      const periods = this.ls.Periods || this.ls.periods || [];
      let activePeriod = periods.find(p => String(p.Type || p.type) === "60");
      if (!activePeriod) {
        const currentPeriodNum = sb?.CurrentPeriod || sb?.currentPeriod;
        activePeriod = periods.find(p => String(p.Type || p.type) === String(currentPeriodNum));
      }
      if (activePeriod) {
        const res = activePeriod.Results || activePeriod.results || [];
        const h = res.find(r => String(r.Position || r.position) === "1");
        const a = res.find(r => String(r.Position || r.position) === "2");
        return {
          h: String(h ? (h.Value ?? h.value ?? "0") : "0"),
          a: String(a ? (a.Value ?? a.value ?? "0") : "0")
        };
      }
      return { h: "0", a: "0" };
    },
    tennisSets() {
      const periods = this.ls?.Periods || this.ls?.periods;
      if (!periods) return [];
      return periods.filter(p => parseInt(p.Type || p.type) < 10).map(p => {
        const res = p.Results || p.results;
        return {
          h: res?.find(r => String(r.Position || r.position) === "1")?.Value ?? res?.find(r => String(r.Position || r.position) === "1")?.value ?? 0,
          a: res?.find(r => String(r.Position || r.position) === "2")?.Value ?? res?.find(r => String(r.Position || r.position) === "2")?.value ?? 0
        };
      });
    },
    periodLabel() {
      const sb = this.ls?.Scoreboard || this.ls?.scoreboard;
      const p = String(sb?.CurrentPeriod || sb?.currentPeriod);
      const sId = String(this.matchData?.sportId || this.matchData?.SportId || "");
      const conf = PERIODS_MAP[sId];
      if (conf && conf[p]) {
        return this.$t(conf[p]);
      }
      const fallbackMap = {
        "10": 'statistics.1t',
        "20": 'statistics.2t',
        "100": 'statistics.fin',
        "1": 'statistics.s1',
        "2": 'statistics.s2',
        "3": 'statistics.s3'
      };
      return p && fallbackMap[p] ? this.$t(fallbackMap[p]) : this.$t('statistics.live');
    },
    sportConfig() {
      const id = parseInt(this.matchData?.Fixture?.Sport?.Id || this.matchData?.sportId || 0);
      const mapping = {
        soccer: [6046, 274792],
        tennis: [54094, 265917],
        basket: [48242, 1149105, 1149097],
        ice: [35232, 530129, 46957],
        cricket: [452674],
        volley: [154830, 621569, 1149113],
        futsal: [1149117, 621561,687887],
        baseball: [154914],
      };
      // Stili base (saranno pompati dal CSS Holographic)
      if (mapping.soccer.includes(id)) return { class: 'f-grass', lines: 'soccer', color: '#10b981' };
      if (mapping.tennis.includes(id)) {
        let surfaceClass = 'f-clay';
        const extra = this.ls?.LivescoreExtraData || this.ls?.livescoreExtraData || [];
        const surfaceObj = extra.find(d => (d.Name || d.name) === 'CourtSurfaceType');
        if (surfaceObj) {
          const surfaceVal = String(surfaceObj.Value || surfaceObj.value).toLowerCase();
          if (surfaceVal.includes('hard')) surfaceClass = 'f-hard';
          if (surfaceVal.includes('grass')) surfaceClass = 'f-grass';
        }
        return { class: surfaceClass, lines: 'tennis', color: surfaceClass === 'f-hard' ? '#3b82f6' : '#f97316' };
      }
      if (mapping.basket.includes(id)) return { class: 'f-court', lines: 'basket', color: '#f59e0b' };
      if (mapping.ice.includes(id)) return { class: 'f-ice', lines: 'hockey', color: '#38bdf8' };
      if (mapping.cricket.includes(id)) return { class: 'f-cricket', lines: 'cricket', color: '#22c55e' };
      if (mapping.volley.includes(id)) return { class: 'f-volley', lines: 'volley', color: '#f97316' };
      if (mapping.futsal.includes(id)) return { class: 'f-futsal', lines: 'futsal', color: '#3b82f6' };
      if (mapping.baseball.includes(id)) return { class: 'f-baseball', lines: 'baseball', color: '#4f9146' };
      return { class: 'f-dark', lines: 'none', color: '#64748b' };
    },
    isSoccerStadium() {
      return this.sportConfig.lines === 'soccer';
    },
    visibleStats() {
      const stats = this.ls?.Statistics || this.ls?.statistics;
      if (!stats) return [];
      return stats.filter(s => this.statNames[parseInt(s.Type || s.type)]).map(s => {
        const res = s.Results || s.results || [];
        const r1 = res.find(r => String(r.Position || r.position) === "1");
        const r2 = res.find(r => String(r.Position || r.position) === "2");
        return {
          type: parseInt(s.Type || s.type),
          v1: r1 ? (r1.Value ?? r1.value ?? 0) : 0,
          v2: r2 ? (r2.Value ?? r2.value ?? 0) : 0
        }
      });
    },
    paginatedStats() {
      const stats = this.visibleStats;
      const chunkSize = 4;
      const pages = [];
      for (let i = 0; i < stats.length; i += chunkSize) {
        pages.push(stats.slice(i, i + chunkSize));
      }
      return pages;
    },
    hasCards() {
      const stats = this.ls?.Statistics || this.ls?.statistics;
      return stats?.some(s => parseInt(s.Type || s.type) === 6 || parseInt(s.Type || s.type) === 7);
    }
  }
}
</script>

<style scoped>
/* ====================================================
   HOLOGRAPHIC AR TRACKER - FINAL CSS COMPACT (OPTIMIZED SPACE)
   ==================================================== */
.ar-tracker-master { position: relative; width: 100%; background: var(--bg-body); border-radius: 0; overflow: hidden;  font-family: 'Roboto', sans-serif; color: #fff; padding-bottom: 0; transition: all 0.3s ease; }
.resize-tracker-btn { position: absolute; top: 60px; right: 10px; z-index: 100; background: rgba(0,0,0,0.5); border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; padding: 4px; cursor: pointer; transition: all 0.2s; }
.resize-tracker-btn:hover { background: rgba(14, 165, 233, 0.2); border-color: #0ea5e9; } /* Azzurro Oceano */
.tracker-custom-tooltip { position: fixed; background: rgba(0,0,0,0.9); color: #fff; padding: 6px 12px; border-radius: 6px; font-size: var(--font-size-xl); font-weight: 700; z-index: 999999; pointer-events: none; border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 4px 15px rgba(0,0,0,0.8); white-space: nowrap; }
.momentum-header { position: relative; border-bottom: 1px solid rgba(255,255,255,0.05); background: var(--bg-body); z-index: 30; padding: 10px 0 0; box-shadow: 0 10px 20px rgba(0,0,0,0.5); overflow: hidden; }

/* ====================================================
   SPLIT LAYOUT LOGIC (FIELD LEFT, CONTENT RIGHT)
   ==================================================== */
.stage-master-container { display: flex; flex-direction: column; width: 100%; transition: all 0.3s ease; }
.stage-master-container.is-split-layout { flex-direction: row; height: 280px; align-items: stretch; }
.stage-master-container.is-split-layout .holographic-stage { width: 45%; height: 100%; border-right: 1px solid rgba(255, 255, 255, 0.05); }
.stage-master-container.is-split-layout .pitch-frame { transform: scale(calc(var(--scene-scale, 0.85) * 0.82)) rotateX(var(--scene-x-rotation, 65deg)) translate3d(var(--scene-x-translate, 0px), var(--scene-y-translate, -3.4px), var(--scene-z-translate, 0px)) !important; transform-origin: 50% 0%; }
.stage-master-container.is-split-layout .stage-secondary-content { width: 55%; height: 100%; min-height: auto; padding: 10px 15px; background: transparent; }
.stage-master-container.is-split-layout .stage-right-inner { padding-right: 5px; }
.stage-master-container.is-split-layout .stage-right-inner::-webkit-scrollbar { width: 4px; }
.stage-master-container.is-split-layout .stage-right-inner::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 4px; }

/* STAGE — low broadcast-camera 3D pitch: a stadium bowl behind an animated
   ad-board strip, then the pitch itself tilted back with a CSS perspective
   (mirroring a real low camera angle). The pitch keeps a fixed height (like
   a broadcast graphic package) instead of an intrinsic aspect-ratio, since
   the tilt itself does the foreshortening. */
.holographic-stage { width: 100%; position: relative; transition: all 0.3s ease; display: flex; justify-content: center; align-items: center; }
.stage-left-pitch { width: 100%; display: flex; align-items: center; justify-content: center; position: relative; }
.pitch-deploy-wrapper { width: 100%; display: flex; justify-content: center; align-items: center; }
.pitch-stage-3d { width: 100%; max-width: 650px; position: relative; perspective: 500px; }

/* Match-time progress: a slim red bar above the clock, filled to the
   elapsed fraction of the sport's total duration (already computed for
   the timeline labels, just not wired to any visual before). */
.match-progress-bar { position: relative; height: 3px; background: rgba(255,255,255,0.1); z-index: 3; }
.match-progress-fill { position: absolute; top: 0; left: 0; bottom: 0; background: linear-gradient(90deg, #dc2626, #ef4444); box-shadow: 0 0 6px rgba(239,68,68,0.6); transition: width 0.6s linear; }

/* Stadium bowl: a curved dark shape with a speckled crowd texture, faded
   out toward the ad-board strip below it. */
.stadium-backdrop { position: relative; height: 64px; display: flex; background: radial-gradient(ellipse 90% 100% at 50% 100%, #26262b 0%, #0a0a0c 75%); border-radius: 50% 50% 0 0 / 100% 100% 0 0; overflow: hidden; z-index: 0; }
/* Rows of individual seats — a grid of tiny blocks (vertical seam +
   horizontal seam) reads as stadium seating, not just static/noise. */
.stadium-backdrop::before {
  content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none;
  background-image:
    repeating-linear-gradient(90deg, rgba(255,255,255,0.32) 0px, rgba(255,255,255,0.32) 2px, transparent 2px, transparent 5px),
    repeating-linear-gradient(0deg, rgba(0,0,0,0.4) 0px, rgba(0,0,0,0.4) 1px, transparent 1px, transparent 4px);
  mask-image: linear-gradient(to bottom, black 0%, black 55%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 0%, black 55%, transparent 100%);
}
/* Left/right stands tinted with each team's own colour (mix-blend-mode
   lets the same hex work as a tint over the dark speckled texture instead
   of needing a hue/saturation pair per team). */
.stadium-backdrop__half { flex: 1; mix-blend-mode: color; opacity: 0.8; }
.stadium-backdrop__half--home { background: var(--color-home, #00e0ff); }
.stadium-backdrop__half--away { background: var(--color-away, #ff3d5a); }

/* Pitch-side advertising board: a looping marquee, same look on every skin
   since it carries no real sponsor data — just a generic "LIVE" ribbon. */
.ad-board-ticker { position: relative; z-index: 2; height: 15px; background: #050505; overflow: hidden; border-top: 1px solid rgba(255,255,255,0.08); }
.ad-board-track { display: flex; white-space: nowrap; width: max-content; animation: ad-scroll 14s linear infinite; }
.ad-board-half { display: flex; gap: 36px; padding-left: 36px; flex-shrink: 0; }
.ad-board-track span { font-size: var(--font-size-xs); font-weight: 800; letter-spacing: 0.12em; color: rgba(255,255,255,0.5); text-transform: uppercase; }
.ad-board-logo { height: 13px; width: auto; object-fit: contain; flex-shrink: 0; }
@keyframes ad-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }

/* The tilted plane: .pitch-container is the clipped grass+lines layer (its
   own overflow:hidden would clip 3D children too, so the floating ball/
   label live as siblings here instead, inheriting the same rotation via
   preserve-3d and counter-rotating individually to stay upright).
   The scene transform is exposed as custom properties (rotation/scale/
   translate) so the tilt can be tuned without touching the transform
   itself. At a steep rotation the element's *rendered* height is far
   shorter than its own box (perspective foreshortening) — margin-bottom
   pulls the following content up to match, instead of leaving a blank gap
   the size of the difference. */
.pitch-frame {
  --scene-x-rotation: 50deg;
  --scene-y-rotation: 0deg;
  --scene-z-rotation: 0deg;
  --scene-scale: 0.85;
  --scene-x-translate: 0px;
  --scene-y-translate: -3.4px;
  --scene-z-translate: 0px;
  position: relative; height: 220px; margin: 0 auto -69px; margin-top: 10px;
  padding: 0 13px; box-sizing: border-box;
  transform: scale(var(--scene-scale)) rotateX(var(--scene-x-rotation)) rotateY(var(--scene-y-rotation)) rotate(var(--scene-z-rotation)) translate3d(var(--scene-x-translate), var(--scene-y-translate), var(--scene-z-translate));
  transform-origin: 50% 0%; transform-style: preserve-3d;
}
.pitch-container { position: absolute; inset: 0; border-radius: 0 0 6px 6px; overflow: hidden; box-shadow: 0 20px 40px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.08); }
/* Side ad-boards sit in the padding gutter .pitch-frame now reserves on
   each side, sharing its 3D plane (no counter-rotation) so they tilt back
   with the grass. A horizontal marquee identical to the top ad-board's,
   just rotated 90deg, reads correctly as a vertical board. */
.side-ad-board { position: absolute; top: 0; bottom: 0; width: 13px; overflow: hidden; background: #050505; z-index: 2; }
.side-ad-board--left { left: 0; border-right: 1px solid rgba(255,255,255,0.08); }
.side-ad-board--right { right: 0; border-left: 1px solid rgba(255,255,255,0.08); }
.side-ad-board-track {
  position: absolute; top: 50%; left: 50%; width: 220px; height: 13px;
  display: flex; align-items: center;
  transform: translate(-50%, -50%) rotate(90deg);
  animation: side-ad-scroll 10s linear infinite;
}
.side-ad-board-half { display: flex; gap: 18px; padding-left: 18px; flex-shrink: 0; align-items: center; }
.side-ad-board-track span { font-size: 7px; font-weight: 800; letter-spacing: 0.1em; color: rgba(255,255,255,0.5); text-transform: uppercase; white-space: nowrap; }
.side-ad-board-logo { height: 11px; width: auto; object-fit: contain; flex-shrink: 0; }
@keyframes side-ad-scroll {
  from { transform: translate(-50%, -50%) rotate(90deg) translateX(0); }
  to { transform: translate(-50%, -50%) rotate(90deg) translateX(-50%); }
}
.pitch-lines { position: absolute; inset: 0; z-index: 1; pointer-events: none; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.5)); }
/* Flat overlay outside the tilted space (see the template comment) —
   positioned relative to .pitch-stage-3d, just below the ad-board strip. */
.pitch-header-badge {
  position: absolute; top: 9px; left: 50%; z-index: 12;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.6);
  border-radius: 10px; padding: 2px 10px; font-size: var(--font-size-xl); color: var(--color-primary-light);
  white-space: nowrap;
}
.pitch-header-badge .badge-period { color: #a3e635; margin-right: 5px; }
.tennis-holo-sets { position: absolute; bottom: 10px; display: flex; gap: 8px; z-index: 10; }
.holo-set-pill { background: rgba(0,0,0,0.7); border: 1px solid rgba(255,255,255,0.2); padding: 4px 8px; border-radius: 12px; font-size: var(--font-size-xl); font-weight: 700; color: #aaa; }
.holo-set-pill span { color: #fff; margin-left: 4px; }

/* CAMPI COLORI — realistic mown-grass stripes for pitch sports, flat court tones for the rest */
.f-grass { background: radial-gradient(ellipse at 50% 50%, #499946 0%, #31672f 100%); }
.f-grass::before { content: ''; position: absolute; inset: 0; z-index: 0; pointer-events: none; background: repeating-linear-gradient(90deg, transparent 0%, transparent 8.33%, #151515 8.33%, #151515 16.66%); opacity: 0.05; }
.f-futsal { background: repeating-linear-gradient(90deg, #2d6fb0 0%, #2d6fb0 8.33%, #2760a0 8.33%, #2760a0 16.66%); }
.f-court { background: linear-gradient(0deg, #2b1704, #4a2807); border: 1px solid rgba(245, 158, 11, 0.4); }
.f-hard { background: linear-gradient(0deg, #071e36, #0c335a); border: 1px solid rgba(59, 130, 246, 0.4); }
.f-clay { background: linear-gradient(0deg, #3d1406, #63220b); border: 1px solid rgba(249, 115, 22, 0.4); }
.f-ice { background: linear-gradient(0deg, #081d29, #0e3045); border: 1px solid rgba(56, 189, 248, 0.4); }
.f-cricket { background: linear-gradient(0deg, #0d2c16, #164f27); border: 1px solid rgba(34, 197, 94, 0.4); }
.f-volley { background: linear-gradient(0deg, #091a24, #0f2b3b); border: 1px solid rgba(249, 115, 22, 0.4); }
/* Momentum cone: a wide coloured wedge pointing toward the goal the team in
   possession is attacking. It's a single element whose clip-path swaps
   between the home/away polygon — both are 6-point shapes in a matching
   order, so the swap itself morphs instead of snapping — and it keeps
   `momentumSide` (sticky, sect in JS) instead of resetting shape whenever
   possession briefly reads as unclear; only opacity drops then. */
.momentum-arrow {
  position: absolute; top: 50%; left: 50%; z-index: 4; width: 100%; height: 100%;
  transform: translate(-50%, -50%);
  transition: clip-path 0.6s ease, opacity 0.4s ease;
  opacity: 0;
}
.momentum-arrow.is-visible { opacity: 0.55; }
.momentum-arrow.glow-home { background: linear-gradient(90deg, var(--color-home, #00e0ff) 0%, transparent 100%); clip-path: polygon(0% 0%, 57% 0%, 66% 50%, 57% 100%, 0% 100%); }
.momentum-arrow.glow-away { background: linear-gradient(270deg, var(--color-away, #ff3d5a) 0%, transparent 100%); clip-path: polygon(100% 0%, 43% 0%, 34% 50%, 43% 100%, 100% 100%); }
/* Pitch-wide possession glow: two permanent layers crossfading via opacity
   (background-image itself can't be transitioned, so swapping one div's
   class would hard-cut instead of reversing smoothly). */
.glow-overlay { position: absolute; inset: 0; z-index: 0; opacity: 0; transition: opacity 0.6s ease; }
.glow-overlay--home { background: linear-gradient(90deg, rgba(0,224,255,0.22) 0%, transparent 50%); }
.glow-overlay--away { background: linear-gradient(270deg, rgba(255,61,90,0.22) 0%, transparent 50%); }
.glow-overlay.is-visible { opacity: 1; animation: pulse-glow 2s infinite; }
@keyframes pulse-glow { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }

/* ANIMAZIONI E BADGE EVENTI (RESTYLING MODERNO) */
.holo-ball {
  position: absolute; z-index: 15; width: 10px; height: 10px;
  transition: left 0.7s cubic-bezier(.25,1,.5,1), top 0.7s cubic-bezier(.25,1,.5,1);
  transform: translate(-50%, -50%) translateZ(4px) rotateX(calc(-1 * var(--scene-x-rotation, 65deg)));
}
.ball-dot { position: relative; z-index: 2; display: block; width: 100%; height: 100%; border-radius: 50%; background: #fff; box-shadow: 0 0 8px rgba(255,255,255,0.9), 0 2px 4px rgba(0,0,0,0.5); }
/* Soft contact shadow beneath the ball */
.holo-ball-shadow { position: absolute; left: 50%; bottom: -2px; width: 10px; height: 3px; transform: translateX(-50%); border-radius: 50%; background: radial-gradient(ellipse at center, rgba(0,0,0,0.5) 0%, transparent 75%); z-index: 0; }
/* Incident strip: a dedicated full-width bar below the pitch, in normal
   flow (not absolutely positioned over the grass) — no horizontal clamping
   needed since it's never wider than its own container, at any viewport
   width. Only the *content* inside (ar-content-fade) crossfades when the
   incident itself changes; the bar stays put. */
.incident-strip {
  width: 100%; min-height: 44px; box-sizing: border-box;
  padding: 8px 14px; background: rgba(0,0,0,0.55);
  display: flex; align-items: center;
}
/* Football: the strip continues the stadium below the pitch — dark turf
   green fading toward the card's bottom, instead of flat black. */
.incident-strip.is-pitch {
  background: linear-gradient(180deg, #24402d 0%, #1a2f22 45%, #111f17 100%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
}
/* Handball: a dark blue band continuing the floor around the court. */
.incident-strip.is-court { background: linear-gradient(180deg, #17405f 0%, #102b40 100%); box-shadow: inset 0 1px 0 rgba(255,255,255,0.05); }
/* Aussie rules: dark turf continuing the oval's near wing. */
.incident-strip.is-oval { background: linear-gradient(180deg, #2a3a2c 0%, #1b241d 100%); box-shadow: inset 0 1px 0 rgba(255,255,255,0.05); }
/* Ice hockey: a dark band under the rink's near boards. */
.incident-strip.is-rink { background: #1f2124; box-shadow: inset 0 1px 0 rgba(255,255,255,0.04); }
.ar-content-fade-enter-active, .ar-content-fade-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.ar-content-fade-enter, .ar-content-fade-leave-to { opacity: 0; transform: translateY(4px); }

/* Minimal broadcast-style incident marker: a coloured dot, a short leader
   bar and a two-line label (bold title + subtitle) — same look for every
   incident type, goals included (a soft pulse on the dot is the only
   celebratory accent for goals). */
.incident-marker { display: flex; align-items: center; gap: 7px; width: 100%; }
.incident-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.incident-bar { width: 2px; height: 28px; border-radius: 1px; flex-shrink: 0; opacity: 0.85; }
.incident-text {
  display: flex; flex-direction: column; text-align: left; white-space: nowrap;
  flex: 1; min-width: 0;
}
.incident-title { font-size: var(--font-size-2xl); font-weight: 800; color: #fff; letter-spacing: 0.2px; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.incident-sub { font-size: var(--font-size-md); font-weight: 600; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; }
/* Away incidents sit on the right, bar on the outer edge. */
.incident-marker.is-away { flex-direction: row-reverse; }
.incident-marker.is-away .incident-text { text-align: right; align-items: flex-end; }
.incident-marker.is-goal .incident-dot { animation: incident-goal-pulse 1s ease-out infinite; }
.incident-marker.is-goal .incident-title { font-size: var(--font-size-3xl); }
@keyframes incident-goal-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.35); } }


/* CONTENUTI SECONDARI DEFAULT */
.stage-secondary-content { width: 100%; min-height: 250px; background: rgba(15, 15, 20, 0.4); padding: 10px; transition: all 0.3s ease; }
.stage-right-inner { width: 100%; height: 100%; overflow-y: auto; }
::v-deep .stats-tab-wrapper, ::v-deep .lineups-container { background: transparent !important; }

/* DOCK FLOATING */
.floating-dock-wrapper { position: absolute; bottom: 4px; left: 0; right: 0; display: flex; justify-content: center; z-index: 50; pointer-events: none; }
.floating-dock { background: rgba(20, 20, 25, 0.95); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); padding: 8px 18px; border-radius: 50px; border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 10px 40px rgba(0,0,0,0.9); pointer-events: auto; display: flex; align-items: center; justify-content: space-between; gap: 22px; overflow-x: auto; max-width: 95%; transition: all 0.3s ease; }
.floating-dock::-webkit-scrollbar { display: none; }
.custom-dock-btn { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px; padding: 4px 2px; border-radius: 30px; background: transparent; border: none; color: #888; cursor: pointer; transition: all 0.2s ease; }
.custom-dock-btn:hover { color: #fff; }
.custom-dock-btn.is-active { color: #fff; }
.dock-icon { font-size: var(--font-size-7xl) !important; color: inherit !important; }
.dock-active-indicator { width: 16px; height: 3px; border-radius: 2px; background: transparent; transition: background 0.2s ease; }
.custom-dock-btn.is-active .dock-active-indicator { background: #fff; }


/* ====================================================
   ADATTAMENTO COMPATTO E TRONCATURA TESTI
   ==================================================== */
.ar-tracker-master.is-compact-mode { padding-bottom: 0px !important; border-radius: 0px !important; }
.ar-tracker-master.is-compact-mode .floating-dock { padding: 6px 12px !important; gap: 14px !important; width: 95% !important; justify-content: space-between !important; }
.ar-tracker-master.is-compact-mode .dock-icon { font-size: var(--font-size-6xl) !important; }
.ar-tracker-master.is-compact-mode .resize-tracker-btn { top: 5px !important; right: 5px !important; padding: 2px !important; }

/* TRONCATURA MARKER IN COMPACT MODE */
.ar-tracker-master.is-compact-mode .incident-title { font-size: var(--font-size-xs) !important; max-width: 140px; overflow: hidden; text-overflow: ellipsis; }
.ar-tracker-master.is-compact-mode .incident-sub { font-size: var(--font-size-xxs) !important; max-width: 140px; overflow: hidden; text-overflow: ellipsis; }
.ar-tracker-master.is-compact-mode .incident-marker.is-goal .incident-title { font-size: var(--font-size-md) !important; }
.ar-tracker-master.is-compact-mode .incident-bar { height: 20px !important; }
.stat-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0; 
  text-align: center;
  padding: 0 4px; 
}

/* ====================================================
   CONTENUTI SECONDARI DEFAULT - FIX SCROLL E ALTEZZA
   ==================================================== */
.stage-secondary-content { 
  width: 100%; 
  height: 280px; /* <--- FIX FONDAMENTALE: Fissa l'altezza uguale a quella del campo 3D */
  background: rgba(15, 15, 20, 0.4); 
  padding: 10px; 
  transition: all 0.3s ease; 
  overflow: hidden; /* <--- Impedisce al componente di allargarsi spingendo la pagina in giù */
}

.stage-right-inner { 
  width: 100%; 
  height: 100%; 
  overflow-y: auto; /* <--- Attiva finalmente lo scroll interno! */
  padding-right: 5px;
}

/* Abbelliamo la scrollbar per renderla invisibile/cyber */
.stage-right-inner::-webkit-scrollbar { 
  width: 4px; 
}
.stage-right-inner::-webkit-scrollbar-track { 
  background: transparent; 
}
.stage-right-inner::-webkit-scrollbar-thumb { 
  background: rgba(255, 255, 255, 0.15); 
  border-radius: 4px; 
}
.stage-right-inner::-webkit-scrollbar-thumb:hover { 
  background: var(--color-home, #0ea5e9); 
}

/* RESPONSIVE MEDIA QUERIES */
@media (max-width: 768px) {
  /* Annulla lo split layout su schermi piccoli */
  .stage-master-container.is-split-layout { flex-direction: column !important; height: auto !important; }
  .stage-master-container.is-split-layout .holographic-stage { width: 100% !important; border-right: none !important; }
  .stage-master-container.is-split-layout .stage-secondary-content { width: 100% !important; }
  .stage-master-container.is-split-layout .pitch-frame { transform: scale(var(--scene-scale, 0.85)) rotateX(var(--scene-x-rotation, 65deg)) translate3d(var(--scene-x-translate, 0px), var(--scene-y-translate, -3.4px), var(--scene-z-translate, 0px)) !important; }
}

@media (max-width: 600px) {
  .ar-tracker-master { padding-bottom: 45px !important; }

  /* Replica il comportamento della modalità compact sul mobile */
  .incident-title { font-size: var(--font-size-xs) !important; max-width: 130px; overflow: hidden; text-overflow: ellipsis; }
  .incident-sub { font-size: var(--font-size-xxs) !important; max-width: 130px; overflow: hidden; text-overflow: ellipsis; }
  .incident-marker.is-goal .incident-title { font-size: var(--font-size-md) !important; }
  .incident-bar { height: 20px !important; }

  .floating-dock { padding: 6px 12px !important; gap: 14px !important; width: 95% !important; justify-content: space-between !important; }
  .dock-icon { font-size: var(--font-size-6xl) !important; }
  .resize-tracker-btn { top: 5px !important; right: 5px !important; padding: 2px !important; }
}
</style>